import { _jsxBranch, _jsxC, componentQrl, inlinedQrl, _jsxQ, _fnSignal, useSignal, isSignal, useComputedQrl, useLexicalScope, useTaskQrl, useOnDocument, useVisibleTaskQrl, _IMMUTABLE, useStylesQrl } from "@builder.io/qwik";
import { Fragment } from "@builder.io/qwik/jsx-runtime";
const styles = `:where(html[dir='ltr']),
:where([data-qwik-toaster][dir='ltr']) {
  --toast-icon-margin-start: -3px;
  --toast-icon-margin-end: 4px;
  --toast-svg-margin-start: -1px;
  --toast-svg-margin-end: 0px;
  --toast-button-margin-start: auto;
  --toast-button-margin-end: 0;
  --toast-close-button-start: 0;
  --toast-close-button-end: unset;
  --toast-close-button-transform: translate(-35%, -35%);
}

:where(html[dir='rtl']),
:where([data-qwik-toaster][dir='rtl']) {
  --toast-icon-margin-start: 4px;
  --toast-icon-margin-end: -3px;
  --toast-svg-margin-start: 0px;
  --toast-svg-margin-end: -1px;
  --toast-button-margin-start: 0;
  --toast-button-margin-end: auto;
  --toast-close-button-start: unset;
  --toast-close-button-end: 0;
  --toast-close-button-transform: translate(35%, -35%);
}

:where([data-qwik-toaster]) {
  position: fixed;
  width: var(--width);
  font-family: ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Helvetica Neue, Arial,
    Noto Sans, sans-serif, Apple Color Emoji, Segoe UI Emoji, Segoe UI Symbol, Noto Color Emoji;
  --gray1: hsl(0, 0%, 99%);
  --gray2: hsl(0, 0%, 97.3%);
  --gray3: hsl(0, 0%, 95.1%);
  --gray4: hsl(0, 0%, 93%);
  --gray5: hsl(0, 0%, 90.9%);
  --gray6: hsl(0, 0%, 88.7%);
  --gray7: hsl(0, 0%, 85.8%);
  --gray8: hsl(0, 0%, 78%);
  --gray9: hsl(0, 0%, 56.1%);
  --gray10: hsl(0, 0%, 52.3%);
  --gray11: hsl(0, 0%, 43.5%);
  --gray12: hsl(0, 0%, 9%);
  --border-radius: 8px;
  box-sizing: border-box;
  padding: 0;
  margin: 0;
  list-style: none;
  outline: none;
  z-index: 999999999;
}

:where([data-qwik-toaster][data-x-position='right']) {
  right: max(var(--offset), env(safe-area-inset-right));
}

:where([data-qwik-toaster][data-x-position='left']) {
  left: max(var(--offset), env(safe-area-inset-left));
}

:where([data-qwik-toaster][data-x-position='center']) {
  left: 50%;
  transform: translateX(-50%);
}

:where([data-qwik-toaster][data-y-position='top']) {
  top: max(var(--offset), env(safe-area-inset-top));
}

:where([data-qwik-toaster][data-y-position='bottom']) {
  bottom: max(var(--offset), env(safe-area-inset-bottom));
}

:where([data-qwik-toast]) {
  --y: translateY(100%);
  --lift-amount: calc(var(--lift) * var(--gap));
  z-index: var(--z-index);
  position: absolute;
  opacity: 0;
  transform: var(--y);
  filter: blur(0);
  /* https://stackoverflow.com/questions/48124372/pointermove-event-not-working-with-touch-why-not */
  touch-action: none;
  transition: transform 400ms, opacity 400ms, height 400ms, box-shadow 200ms;
  box-sizing: border-box;
  outline: none;
  overflow-wrap: anywhere;
}

:where([data-qwik-toast][data-styled='true']) {
  padding: 16px;
  background: var(--normal-bg);
  border: 1px solid var(--normal-border);
  color: var(--normal-text);
  border-radius: var(--border-radius);
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.1);
  width: var(--width);
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 6px;
}

:where([data-qwik-toast]:focus-visible) {
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.1), 0 0 0 2px rgba(0, 0, 0, 0.2);
}

:where([data-qwik-toast][data-y-position='top']) {
  top: 0;
  --y: translateY(-100%);
  --lift: 1;
  --lift-amount: calc(1 * var(--gap));
}

:where([data-qwik-toast][data-y-position='bottom']) {
  bottom: 0;
  --y: translateY(100%);
  --lift: -1;
  --lift-amount: calc(var(--lift) * var(--gap));
}

:where([data-qwik-toast]) :where([data-description]) {
  font-weight: 400;
  line-height: 1.4;
  color: inherit;
}

:where([data-qwik-toast]) :where([data-title]) {
  font-weight: 500;
  line-height: 1.5;
  color: inherit;
}

:where([data-qwik-toast]) :where([data-icon]) {
  display: flex;
  height: 16px;
  width: 16px;
  position: relative;
  justify-content: flex-start;
  align-items: center;
  flex-shrink: 0;
  margin-left: var(--toast-icon-margin-start);
  margin-right: var(--toast-icon-margin-end);
}

:where([data-qwik-toast][data-promise='true']) :where([data-icon])>svg {
  opacity: 0;
  transform: scale(0.8);
  transform-origin: center;
  animation: qwik-fade-in 300ms ease forwards;
}

:where([data-qwik-toast]) :where([data-icon])>* {
  flex-shrink: 0;
}

:where([data-qwik-toast]) :where([data-icon]) svg {
  margin-left: var(--toast-svg-margin-start);
  margin-right: var(--toast-svg-margin-end);
}

:where([data-qwik-toast]) :where([data-content]) {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

[data-qwik-toast][data-styled="true"] [data-button] {
  border-radius: 4px;
  padding-left: 8px;
  padding-right: 8px;
  height: 24px;
  font-size: 12px;
  color: var(--normal-bg);
  background: var(--normal-text);
  margin-left: var(--toast-button-margin-start);
  margin-right: var(--toast-button-margin-end);
  border: none;
  cursor: pointer;
  outline: none;
  display: flex;
  align-items: center;
  flex-shrink: 0;
  transition: opacity 400ms, box-shadow 200ms;
}

:where([data-qwik-toast]) :where([data-button]):focus-visible {
  box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.4);
}

:where([data-qwik-toast]) :where([data-button]):first-of-type {
  margin-left: var(--toast-button-margin-start);
  margin-right: var(--toast-button-margin-end);
}

:where([data-qwik-toast]) :where([data-cancel]) {
  color: var(--normal-text);
  background: rgba(0, 0, 0, 0.08);
}

:where([data-qwik-toast][data-theme='dark']) :where([data-cancel]) {
  background: rgba(255, 255, 255, 0.3);
}

:where([data-qwik-toast]) :where([data-close-button]) {
  position: absolute;
  left: var(--toast-close-button-start);
  right: var(--toast-close-button-end);
  top: 0;
  height: 20px;
  width: 20px;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0;
  background: var(--gray1);
  color: var(--gray12);
  border: 1px solid var(--gray4);
  transform: var(--toast-close-button-transform);
  border-radius: 50%;
  cursor: pointer;
  z-index: 1;
  transition: opacity 100ms, background 200ms, border-color 200ms;
}

:where([data-qwik-toast]) :where([data-close-button]):focus-visible {
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.1), 0 0 0 2px rgba(0, 0, 0, 0.2);
}

:where([data-qwik-toast]) :where([data-disabled='true']) {
  cursor: not-allowed;
}

:where([data-qwik-toast]):hover :where([data-close-button]):hover {
  background: var(--gray2);
  border-color: var(--gray5);
}

/* Leave a ghost div to avoid setting hover to false when swiping out */
:where([data-qwik-toast][data-swiping='true'])::before {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  height: 100%;
  z-index: -1;
}

:where([data-qwik-toast][data-y-position='top'][data-swiping='true'])::before {
  /* y 50% needed to distribute height additional height evenly */
  bottom: 50%;
  transform: scaleY(3) translateY(50%);
}

:where([data-qwik-toast][data-y-position='bottom'][data-swiping='true'])::before {
  /* y -50% needed to distribute height additional height evenly */
  top: 50%;
  transform: scaleY(3) translateY(-50%);
}

/* Leave a ghost div to avoid setting hover to false when transitioning out */
:where([data-qwik-toast][data-swiping='false'][data-removed='true'])::before {
  content: '';
  position: absolute;
  inset: 0;
  transform: scaleY(2);
}

/* Needed to avoid setting hover to false when inbetween toasts */
:where([data-qwik-toast])::after {
  content: '';
  position: absolute;
  left: 0;
  height: calc(var(--gap) + 1px);
  bottom: 100%;
  width: 100%;
}

:where([data-qwik-toast][data-mounted='true']) {
  --y: translateY(0);
  opacity: 1;
}

:where([data-qwik-toast][data-expanded='false'][data-front='false']) {
  --scale: var(--toasts-before) * 0.05 + 1;
  --y: translateY(calc(var(--lift-amount) * var(--toasts-before))) scale(calc(-1 * var(--scale)));
  height: var(--front-toast-height);
}

:where([data-qwik-toast])>* {
  transition: opacity 400ms;
}

:where([data-qwik-toast][data-expanded='false'][data-front='false'][data-styled='true'])>* {
  opacity: 0;
}

:where([data-qwik-toast][data-visible='false']) {
  opacity: 0;
  pointer-events: none;
}

:where([data-qwik-toast][data-mounted='true'][data-expanded='true']) {
  --y: translateY(calc(var(--lift) * var(--offset)));
  height: var(--initial-height);
}

:where([data-qwik-toast][data-removed='true'][data-front='true'][data-swipe-out='false']) {
  --y: translateY(calc(var(--lift) * -100%));
  opacity: 0;
}

:where([data-qwik-toast][data-removed='true'][data-front='false'][data-swipe-out='false'][data-expanded='true']) {
  --y: translateY(calc(var(--lift) * var(--offset) + var(--lift) * -100%));
  opacity: 0;
}

:where([data-qwik-toast][data-removed='true'][data-front='false'][data-swipe-out='false'][data-expanded='false']) {
  --y: translateY(40%);
  opacity: 0;
  transition: transform 500ms, opacity 200ms;
}

/* Bump up the height to make sure hover state doesn't get set to false */
:where([data-qwik-toast][data-removed='true'][data-front='false'])::before {
  height: calc(var(--initial-height) + 20%);
}

[data-qwik-toast][data-swiping='true'] {
  transform: var(--y) translateY(var(--swipe-amount, 0px));
  transition: none;
}

[data-qwik-toast][data-swipe-out='true'][data-y-position='bottom'],
[data-qwik-toast][data-swipe-out='true'][data-y-position='top'] {
  animation: swipe-out 200ms ease-out forwards;
}

@keyframes swipe-out {
  from {
    transform: translateY(calc(var(--lift) * var(--offset) + var(--swipe-amount)));
    opacity: 1;
  }

  to {
    transform: translateY(calc(var(--lift) * var(--offset) + var(--swipe-amount) + var(--lift) * -100%));
    opacity: 0;
  }
}

@media (max-width: 600px) {
  [data-qwik-toaster] {
    position: fixed;
    --mobile-offset: 16px;
    right: var(--mobile-offset);
    left: var(--mobile-offset);
    width: 100%;
  }

  [data-qwik-toaster] [data-qwik-toast] {
    left: 0;
    right: 0;
    width: calc(100% - var(--mobile-offset) * 2);
  }

  [data-qwik-toaster][data-x-position='left'] {
    left: var(--mobile-offset);
  }

  [data-qwik-toaster][data-y-position='bottom'] {
    bottom: 20px;
  }

  [data-qwik-toaster][data-y-position='top'] {
    top: 20px;
  }

  [data-qwik-toaster][data-x-position='center'] {
    left: var(--mobile-offset);
    right: var(--mobile-offset);
    transform: none;
  }
}

[data-qwik-toaster][data-theme='light'] {
  --normal-bg: #fff;
  --normal-border: var(--gray4);
  --normal-text: var(--gray12);

  --success-bg: hsl(143, 85%, 96%);
  --success-border: hsl(145, 92%, 91%);
  --success-text: hsl(140, 100%, 27%);

  --info-bg: hsl(208, 100%, 97%);
  --info-border: hsl(221, 91%, 91%);
  --info-text: hsl(210, 92%, 45%);

  --warning-bg: hsl(49, 100%, 97%);
  --warning-border: hsl(49, 91%, 91%);
  --warning-text: hsl(31, 92%, 45%);

  --error-bg: hsl(359, 100%, 97%);
  --error-border: hsl(359, 100%, 94%);
  --error-text: hsl(360, 100%, 45%);
}

[data-qwik-toaster][data-theme='light'] [data-qwik-toast][data-invert='true'] {
  --normal-bg: #000;
  --normal-border: hsl(0, 0%, 20%);
  --normal-text: var(--gray1);
}

[data-qwik-toaster][data-theme='dark'] [data-qwik-toast][data-invert='true'] {
  --normal-bg: #fff;
  --normal-border: var(--gray3);
  --normal-text: var(--gray12);
}

[data-qwik-toaster][data-theme='dark'] {
  --normal-bg: #000;
  --normal-border: hsl(0, 0%, 20%);
  --normal-text: var(--gray1);

  --success-bg: hsl(150, 100%, 6%);
  --success-border: hsl(147, 100%, 12%);
  --success-text: hsl(150, 86%, 65%);

  --info-bg: hsl(215, 100%, 6%);
  --info-border: hsl(223, 100%, 12%);
  --info-text: hsl(216, 87%, 65%);

  --warning-bg: hsl(64, 100%, 6%);
  --warning-border: hsl(60, 100%, 12%);
  --warning-text: hsl(46, 87%, 65%);

  --error-bg: hsl(358, 76%, 10%);
  --error-border: hsl(357, 89%, 16%);
  --error-text: hsl(358, 100%, 81%);
}

[data-rich-colors='true'] [data-qwik-toast][data-type='success'] {
  background: var(--success-bg);
  border-color: var(--success-border);
  color: var(--success-text);
}

[data-rich-colors='true'] [data-qwik-toast][data-type='success'] [data-close-button] {
  background: var(--success-bg);
  border-color: var(--success-border);
  color: var(--success-text);
}

[data-rich-colors='true'] [data-qwik-toast][data-type='info'] {
  background: var(--info-bg);
  border-color: var(--info-border);
  color: var(--info-text);
}

[data-rich-colors='true'] [data-qwik-toast][data-type='info'] [data-close-button] {
  background: var(--info-bg);
  border-color: var(--info-border);
  color: var(--info-text);
}

[data-rich-colors='true'] [data-qwik-toast][data-type='warning'] {
  background: var(--warning-bg);
  border-color: var(--warning-border);
  color: var(--warning-text);
}

[data-rich-colors='true'] [data-qwik-toast][data-type='warning'] [data-close-button] {
  background: var(--warning-bg);
  border-color: var(--warning-border);
  color: var(--warning-text);
}

[data-rich-colors='true'] [data-qwik-toast][data-type='error'] {
  background: var(--error-bg);
  border-color: var(--error-border);
  color: var(--error-text);
}

[data-rich-colors='true'] [data-qwik-toast][data-type='error'] [data-close-button] {
  background: var(--error-bg);
  border-color: var(--error-border);
  color: var(--error-text);
}

.qwik-loading-wrapper {
  --size: 16px;
  height: var(--size);
  width: var(--size);
  position: absolute;
  inset: 0;
  z-index: 10;
}

.qwik-loading-wrapper[data-visible='false'] {
  transform-origin: center;
  animation: qwik-fade-out 0.2s ease forwards;
}

.qwik-spinner {
  position: relative;
  top: 50%;
  left: 50%;
  height: var(--size);
  width: var(--size);
}

.qwik-loading-bar {
  animation: qwik-spin 1.2s linear infinite;
  background: var(--gray11);
  border-radius: 6px;
  height: 8%;
  left: -10%;
  position: absolute;
  top: -3.9%;
  width: 24%;
}

.qwik-loading-bar:nth-child(1) {
  animation-delay: -1.2s;
  transform: rotate(0.0001deg) translate(146%);
}

.qwik-loading-bar:nth-child(2) {
  animation-delay: -1.1s;
  transform: rotate(30deg) translate(146%);
}

.qwik-loading-bar:nth-child(3) {
  animation-delay: -1s;
  transform: rotate(60deg) translate(146%);
}

.qwik-loading-bar:nth-child(4) {
  animation-delay: -0.9s;
  transform: rotate(90deg) translate(146%);
}

.qwik-loading-bar:nth-child(5) {
  animation-delay: -0.8s;
  transform: rotate(120deg) translate(146%);
}

.qwik-loading-bar:nth-child(6) {
  animation-delay: -0.7s;
  transform: rotate(150deg) translate(146%);
}

.qwik-loading-bar:nth-child(7) {
  animation-delay: -0.6s;
  transform: rotate(180deg) translate(146%);
}

.qwik-loading-bar:nth-child(8) {
  animation-delay: -0.5s;
  transform: rotate(210deg) translate(146%);
}

.qwik-loading-bar:nth-child(9) {
  animation-delay: -0.4s;
  transform: rotate(240deg) translate(146%);
}

.qwik-loading-bar:nth-child(10) {
  animation-delay: -0.3s;
  transform: rotate(270deg) translate(146%);
}

.qwik-loading-bar:nth-child(11) {
  animation-delay: -0.2s;
  transform: rotate(300deg) translate(146%);
}

.qwik-loading-bar:nth-child(12) {
  animation-delay: -0.1s;
  transform: rotate(330deg) translate(146%);
}

@keyframes qwik-fade-in {
  0% {
    opacity: 0;
    transform: scale(0.8);
  }

  100% {
    opacity: 1;
    transform: scale(1);
  }
}

@keyframes qwik-fade-out {
  0% {
    opacity: 1;
    transform: scale(1);
  }

  100% {
    opacity: 0;
    transform: scale(0.8);
  }
}

@keyframes qwik-spin {
  0% {
    opacity: 1;
  }

  100% {
    opacity: 0.15;
  }
}

@media (prefers-reduced-motion) {

  [data-qwik-toast],
  [data-qwik-toast]>*,
  .qwik-loading-bar {
    transition: none !important;
    animation: none !important;
  }
}

.qwik-loader {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  transform-origin: center;
  transition: opacity 200ms, transform 200ms;
}

.qwik-loader[data-visible='false'] {
  opacity: 0;
  transform: scale(0.8) translate(-50%, -50%);
}`;
const VISIBLE_TOASTS_AMOUNT = 3;
const VIEWPORT_OFFSET = "32px";
const TOAST_LIFETIME = 4e3;
const TOAST_WIDTH = 356;
const GAP = 14;
const SWIPE_THRESHOLD = 20;
const TIME_BEFORE_UNMOUNT = 200;
let toastsCounter = 1;
const subscribers = [];
let toasts = [];
const subscribe = (subscriber) => {
  subscribers.push(subscriber);
  return () => {
    const index = subscribers.indexOf(subscriber);
    subscribers.splice(index, 1);
  };
};
const publish = (data) => {
  subscribers.forEach((subscriber) => subscriber(data));
};
const addToast = (data) => {
  publish(data);
  toasts = [
    ...toasts,
    data
  ];
};
const create$1 = (data) => {
  const { message: message2, ...rest } = data;
  const id = typeof data?.id === "number" || data.id && data.id?.length > 0 ? data.id : toastsCounter++;
  const alreadyExists = toasts.find((toast2) => {
    return toast2.id === id;
  });
  const dismissible = data.dismissible === void 0 ? true : data.dismissible;
  if (alreadyExists)
    toasts = toasts.map((toast2) => {
      if (toast2.id === id) {
        publish({
          ...toast2,
          ...data,
          id,
          title: message2
        });
        return {
          ...toast2,
          ...data,
          id,
          dismissible,
          title: message2
        };
      }
      return toast2;
    });
  else
    addToast({
      title: message2,
      ...rest,
      dismissible,
      id
    });
  return id;
};
const dismiss = (id) => {
  if (!id)
    return toasts.forEach((toast2) => {
      subscribers.forEach((subscriber) => subscriber({
        id: toast2.id,
        dismiss: true
      }));
    });
  subscribers.forEach((subscriber) => subscriber({
    id,
    dismiss: true
  }));
  return id;
};
const message = (message2, data) => {
  return create$1({
    ...data,
    message: message2
  });
};
const error = (message2, data) => {
  return create$1({
    ...data,
    message: message2,
    type: "error"
  });
};
const success = (message2, data) => {
  return create$1({
    ...data,
    type: "success",
    message: message2
  });
};
const info = (message2, data) => {
  return create$1({
    ...data,
    type: "info",
    message: message2
  });
};
const warning = (message2, data) => {
  return create$1({
    ...data,
    type: "warning",
    message: message2
  });
};
const loading = (message2, data) => {
  return create$1({
    ...data,
    type: "loading",
    message: message2
  });
};
const promise = (promise2, data) => {
  if (!data)
    return;
  let id = void 0;
  if (data.loading !== void 0)
    id = create$1({
      ...data,
      promise: promise2,
      type: "loading",
      message: data.loading,
      description: typeof data.description !== "function" ? data.description : void 0
    });
  const p = promise2 instanceof Promise ? promise2 : promise2();
  let shouldDismiss = id !== void 0;
  p.then(async (response) => {
    if (response && typeof response.ok === "boolean" && !response.ok) {
      shouldDismiss = false;
      const message2 = typeof data.error === "function" ? await data.error({
        // @ts-expect-error
        error: `HTTP error! status: ${response.status}`
      }) : data.error;
      const description = typeof data.description === "function" ? await data.description(
        // @ts-expect-error
        `HTTP error! status: ${response.status}`
      ) : data.description;
      create$1({
        id,
        type: "error",
        message: message2,
        description
      });
    } else if (data.success !== void 0) {
      shouldDismiss = false;
      const message2 = typeof data.success === "function" ? await data.success(response) : data.success;
      const description = typeof data.description === "function" ? await data.description(response) : data.description;
      create$1({
        id,
        type: "success",
        message: message2,
        description
      });
    }
  }).catch(async (error2) => {
    if (data.error !== void 0) {
      shouldDismiss = false;
      const message2 = typeof data.error === "function" ? await data.error(error2) : data.error;
      const description = typeof data.description === "function" ? await data.description(error2) : data.description;
      create$1({
        id,
        type: "error",
        message: message2,
        description
      });
    }
  }).finally(() => {
    if (shouldDismiss) {
      dismiss(id);
      id = void 0;
    }
    data.finally?.();
  });
  return id;
};
const custom = (jsx, data) => {
  const id = data?.id || toastsCounter++;
  create$1({
    jsx: jsx(id),
    id,
    ...data
  });
  return id;
};
const ToastState = {
  subscribe,
  addToast,
  create: create$1,
  dismiss,
  message,
  error,
  success,
  info,
  warning,
  loading,
  promise,
  custom
};
const toastFunction = (message2, data) => {
  const id = data?.id || toastsCounter++;
  ToastState.addToast({
    title: message2,
    ...data,
    id
  });
  return id;
};
const basicToast = toastFunction;
const toast = Object.assign(basicToast, {
  success: ToastState.success,
  info: ToastState.info,
  warning: ToastState.warning,
  error: ToastState.error,
  custom: ToastState.custom,
  message: ToastState.message,
  promise: ToastState.promise,
  dismiss: ToastState.dismiss,
  loading: ToastState.loading
});
function isAction(action) {
  return action.label !== void 0 && typeof action.onClick$ === "function";
}
let SwipeStateTypes;
(function(SwipeStateTypes2) {
  SwipeStateTypes2["SwipedOut"] = "SwipedOut";
  SwipeStateTypes2["SwipedBack"] = "SwipedBack";
  SwipeStateTypes2["NotSwiped"] = "NotSwiped";
})(SwipeStateTypes || (SwipeStateTypes = {}));
const getAsset = (type) => {
  _jsxBranch();
  switch (type) {
    case "success":
      return /* @__PURE__ */ _jsxC(SuccessIcon, null, 3, "oH_0");
    case "info":
      return /* @__PURE__ */ _jsxC(InfoIcon, null, 3, "oH_1");
    case "warning":
      return /* @__PURE__ */ _jsxC(WarningIcon, null, 3, "oH_2");
    case "error":
      return /* @__PURE__ */ _jsxC(ErrorIcon, null, 3, "oH_3");
    default:
      return null;
  }
};
const bars = Array(12).fill(0);
const Loader = /* @__PURE__ */ componentQrl(/* @__PURE__ */ inlinedQrl((props) => {
  return /* @__PURE__ */ _jsxQ("div", null, {
    class: "qwik-loading-wrapper",
    "data-visible": _fnSignal((p0) => String(p0.visible), [
      props
    ], "String(p0.visible)")
  }, /* @__PURE__ */ _jsxQ("div", null, {
    class: "qwik-spinner"
  }, bars.map((_, i) => /* @__PURE__ */ _jsxQ("div", null, {
    class: "qwik-loading-bar"
  }, null, 3, `spinner-bar-${i}`)), 1, null), 1, "oH_4");
}, "Loader_component_0409WLJe4ZA"));
const SuccessIcon = /* @__PURE__ */ componentQrl(/* @__PURE__ */ inlinedQrl(() => /* @__PURE__ */ _jsxQ("svg", null, {
  xmlns: "http://www.w3.org/2000/svg",
  viewBox: "0 0 20 20",
  fill: "currentColor",
  height: "20",
  width: "20"
}, /* @__PURE__ */ _jsxQ("path", null, {
  "fill-rule": "evenodd",
  d: "M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z",
  "clip-rule": "evenodd"
}, null, 3, null), 3, "oH_5"), "SuccessIcon_component_PlMhMWQHW9g"));
const WarningIcon = /* @__PURE__ */ componentQrl(/* @__PURE__ */ inlinedQrl(() => /* @__PURE__ */ _jsxQ("svg", null, {
  xmlns: "http://www.w3.org/2000/svg",
  viewBox: "0 0 24 24",
  fill: "currentColor",
  height: "20",
  width: "20"
}, /* @__PURE__ */ _jsxQ("path", null, {
  "fill-rule": "evenodd",
  d: "M9.401 3.003c1.155-2 4.043-2 5.197 0l7.355 12.748c1.154 2-.29 4.5-2.599 4.5H4.645c-2.309 0-3.752-2.5-2.598-4.5L9.4 3.003zM12 8.25a.75.75 0 01.75.75v3.75a.75.75 0 01-1.5 0V9a.75.75 0 01.75-.75zm0 8.25a.75.75 0 100-1.5.75.75 0 000 1.5z",
  "clip-rule": "evenodd"
}, null, 3, null), 3, "oH_6"), "WarningIcon_component_XugbeigHGlc"));
const InfoIcon = /* @__PURE__ */ componentQrl(/* @__PURE__ */ inlinedQrl(() => /* @__PURE__ */ _jsxQ("svg", null, {
  xmlns: "http://www.w3.org/2000/svg",
  viewBox: "0 0 20 20",
  fill: "currentColor",
  height: "20",
  width: "20"
}, /* @__PURE__ */ _jsxQ("path", null, {
  "fill-rule": "evenodd",
  d: "M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a.75.75 0 000 1.5h.253a.25.25 0 01.244.304l-.459 2.066A1.75 1.75 0 0010.747 15H11a.75.75 0 000-1.5h-.253a.25.25 0 01-.244-.304l.459-2.066A1.75 1.75 0 009.253 9H9z",
  "clip-rule": "evenodd"
}, null, 3, null), 3, "oH_7"), "InfoIcon_component_czjMKqmqjMo"));
const ErrorIcon = /* @__PURE__ */ componentQrl(/* @__PURE__ */ inlinedQrl(() => /* @__PURE__ */ _jsxQ("svg", null, {
  xmlns: "http://www.w3.org/2000/svg",
  viewBox: "0 0 20 20",
  fill: "currentColor",
  height: "20",
  width: "20"
}, /* @__PURE__ */ _jsxQ("path", null, {
  "fill-rule": "evenodd",
  d: "M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-5a.75.75 0 01.75.75v4.5a.75.75 0 01-1.5 0v-4.5A.75.75 0 0110 5zm0 10a1 1 0 100-2 1 1 0 000 2z",
  "clip-rule": "evenodd"
}, null, 3, null), 3, "oH_8"), "ErrorIcon_component_uE2rykD0NII"));
/*! @license DOMPurify 3.1.2 | (c) Cure53 and other contributors | Released under the Apache license 2.0 and Mozilla Public License 2.0 | github.com/cure53/DOMPurify/blob/3.1.2/LICENSE */
const {
  entries,
  setPrototypeOf,
  isFrozen,
  getPrototypeOf,
  getOwnPropertyDescriptor
} = Object;
let {
  freeze,
  seal,
  create
} = Object;
let {
  apply,
  construct
} = typeof Reflect !== "undefined" && Reflect;
if (!freeze) {
  freeze = function freeze2(x) {
    return x;
  };
}
if (!seal) {
  seal = function seal2(x) {
    return x;
  };
}
if (!apply) {
  apply = function apply2(fun, thisValue, args) {
    return fun.apply(thisValue, args);
  };
}
if (!construct) {
  construct = function construct2(Func, args) {
    return new Func(...args);
  };
}
const arrayForEach = unapply(Array.prototype.forEach);
const arrayPop = unapply(Array.prototype.pop);
const arrayPush = unapply(Array.prototype.push);
const stringToLowerCase = unapply(String.prototype.toLowerCase);
const stringToString = unapply(String.prototype.toString);
const stringMatch = unapply(String.prototype.match);
const stringReplace = unapply(String.prototype.replace);
const stringIndexOf = unapply(String.prototype.indexOf);
const stringTrim = unapply(String.prototype.trim);
const objectHasOwnProperty = unapply(Object.prototype.hasOwnProperty);
const regExpTest = unapply(RegExp.prototype.test);
const typeErrorCreate = unconstruct(TypeError);
function unapply(func) {
  return function(thisArg) {
    for (var _len = arguments.length, args = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
      args[_key - 1] = arguments[_key];
    }
    return apply(func, thisArg, args);
  };
}
function unconstruct(func) {
  return function() {
    for (var _len2 = arguments.length, args = new Array(_len2), _key2 = 0; _key2 < _len2; _key2++) {
      args[_key2] = arguments[_key2];
    }
    return construct(func, args);
  };
}
function addToSet(set, array) {
  let transformCaseFunc = arguments.length > 2 && arguments[2] !== void 0 ? arguments[2] : stringToLowerCase;
  if (setPrototypeOf) {
    setPrototypeOf(set, null);
  }
  let l = array.length;
  while (l--) {
    let element = array[l];
    if (typeof element === "string") {
      const lcElement = transformCaseFunc(element);
      if (lcElement !== element) {
        if (!isFrozen(array)) {
          array[l] = lcElement;
        }
        element = lcElement;
      }
    }
    set[element] = true;
  }
  return set;
}
function cleanArray(array) {
  for (let index = 0; index < array.length; index++) {
    const isPropertyExist = objectHasOwnProperty(array, index);
    if (!isPropertyExist) {
      array[index] = null;
    }
  }
  return array;
}
function clone(object) {
  const newObject = create(null);
  for (const [property, value] of entries(object)) {
    const isPropertyExist = objectHasOwnProperty(object, property);
    if (isPropertyExist) {
      if (Array.isArray(value)) {
        newObject[property] = cleanArray(value);
      } else if (value && typeof value === "object" && value.constructor === Object) {
        newObject[property] = clone(value);
      } else {
        newObject[property] = value;
      }
    }
  }
  return newObject;
}
function lookupGetter(object, prop) {
  while (object !== null) {
    const desc = getOwnPropertyDescriptor(object, prop);
    if (desc) {
      if (desc.get) {
        return unapply(desc.get);
      }
      if (typeof desc.value === "function") {
        return unapply(desc.value);
      }
    }
    object = getPrototypeOf(object);
  }
  function fallbackValue() {
    return null;
  }
  return fallbackValue;
}
const html$1 = freeze(["a", "abbr", "acronym", "address", "area", "article", "aside", "audio", "b", "bdi", "bdo", "big", "blink", "blockquote", "body", "br", "button", "canvas", "caption", "center", "cite", "code", "col", "colgroup", "content", "data", "datalist", "dd", "decorator", "del", "details", "dfn", "dialog", "dir", "div", "dl", "dt", "element", "em", "fieldset", "figcaption", "figure", "font", "footer", "form", "h1", "h2", "h3", "h4", "h5", "h6", "head", "header", "hgroup", "hr", "html", "i", "img", "input", "ins", "kbd", "label", "legend", "li", "main", "map", "mark", "marquee", "menu", "menuitem", "meter", "nav", "nobr", "ol", "optgroup", "option", "output", "p", "picture", "pre", "progress", "q", "rp", "rt", "ruby", "s", "samp", "section", "select", "shadow", "small", "source", "spacer", "span", "strike", "strong", "style", "sub", "summary", "sup", "table", "tbody", "td", "template", "textarea", "tfoot", "th", "thead", "time", "tr", "track", "tt", "u", "ul", "var", "video", "wbr"]);
const svg$1 = freeze(["svg", "a", "altglyph", "altglyphdef", "altglyphitem", "animatecolor", "animatemotion", "animatetransform", "circle", "clippath", "defs", "desc", "ellipse", "filter", "font", "g", "glyph", "glyphref", "hkern", "image", "line", "lineargradient", "marker", "mask", "metadata", "mpath", "path", "pattern", "polygon", "polyline", "radialgradient", "rect", "stop", "style", "switch", "symbol", "text", "textpath", "title", "tref", "tspan", "view", "vkern"]);
const svgFilters = freeze(["feBlend", "feColorMatrix", "feComponentTransfer", "feComposite", "feConvolveMatrix", "feDiffuseLighting", "feDisplacementMap", "feDistantLight", "feDropShadow", "feFlood", "feFuncA", "feFuncB", "feFuncG", "feFuncR", "feGaussianBlur", "feImage", "feMerge", "feMergeNode", "feMorphology", "feOffset", "fePointLight", "feSpecularLighting", "feSpotLight", "feTile", "feTurbulence"]);
const svgDisallowed = freeze(["animate", "color-profile", "cursor", "discard", "font-face", "font-face-format", "font-face-name", "font-face-src", "font-face-uri", "foreignobject", "hatch", "hatchpath", "mesh", "meshgradient", "meshpatch", "meshrow", "missing-glyph", "script", "set", "solidcolor", "unknown", "use"]);
const mathMl$1 = freeze(["math", "menclose", "merror", "mfenced", "mfrac", "mglyph", "mi", "mlabeledtr", "mmultiscripts", "mn", "mo", "mover", "mpadded", "mphantom", "mroot", "mrow", "ms", "mspace", "msqrt", "mstyle", "msub", "msup", "msubsup", "mtable", "mtd", "mtext", "mtr", "munder", "munderover", "mprescripts"]);
const mathMlDisallowed = freeze(["maction", "maligngroup", "malignmark", "mlongdiv", "mscarries", "mscarry", "msgroup", "mstack", "msline", "msrow", "semantics", "annotation", "annotation-xml", "mprescripts", "none"]);
const text = freeze(["#text"]);
const html = freeze(["accept", "action", "align", "alt", "autocapitalize", "autocomplete", "autopictureinpicture", "autoplay", "background", "bgcolor", "border", "capture", "cellpadding", "cellspacing", "checked", "cite", "class", "clear", "color", "cols", "colspan", "controls", "controlslist", "coords", "crossorigin", "datetime", "decoding", "default", "dir", "disabled", "disablepictureinpicture", "disableremoteplayback", "download", "draggable", "enctype", "enterkeyhint", "face", "for", "headers", "height", "hidden", "high", "href", "hreflang", "id", "inputmode", "integrity", "ismap", "kind", "label", "lang", "list", "loading", "loop", "low", "max", "maxlength", "media", "method", "min", "minlength", "multiple", "muted", "name", "nonce", "noshade", "novalidate", "nowrap", "open", "optimum", "pattern", "placeholder", "playsinline", "poster", "preload", "pubdate", "radiogroup", "readonly", "rel", "required", "rev", "reversed", "role", "rows", "rowspan", "spellcheck", "scope", "selected", "shape", "size", "sizes", "span", "srclang", "start", "src", "srcset", "step", "style", "summary", "tabindex", "title", "translate", "type", "usemap", "valign", "value", "width", "wrap", "xmlns", "slot"]);
const svg = freeze(["accent-height", "accumulate", "additive", "alignment-baseline", "ascent", "attributename", "attributetype", "azimuth", "basefrequency", "baseline-shift", "begin", "bias", "by", "class", "clip", "clippathunits", "clip-path", "clip-rule", "color", "color-interpolation", "color-interpolation-filters", "color-profile", "color-rendering", "cx", "cy", "d", "dx", "dy", "diffuseconstant", "direction", "display", "divisor", "dur", "edgemode", "elevation", "end", "fill", "fill-opacity", "fill-rule", "filter", "filterunits", "flood-color", "flood-opacity", "font-family", "font-size", "font-size-adjust", "font-stretch", "font-style", "font-variant", "font-weight", "fx", "fy", "g1", "g2", "glyph-name", "glyphref", "gradientunits", "gradienttransform", "height", "href", "id", "image-rendering", "in", "in2", "k", "k1", "k2", "k3", "k4", "kerning", "keypoints", "keysplines", "keytimes", "lang", "lengthadjust", "letter-spacing", "kernelmatrix", "kernelunitlength", "lighting-color", "local", "marker-end", "marker-mid", "marker-start", "markerheight", "markerunits", "markerwidth", "maskcontentunits", "maskunits", "max", "mask", "media", "method", "mode", "min", "name", "numoctaves", "offset", "operator", "opacity", "order", "orient", "orientation", "origin", "overflow", "paint-order", "path", "pathlength", "patterncontentunits", "patterntransform", "patternunits", "points", "preservealpha", "preserveaspectratio", "primitiveunits", "r", "rx", "ry", "radius", "refx", "refy", "repeatcount", "repeatdur", "restart", "result", "rotate", "scale", "seed", "shape-rendering", "specularconstant", "specularexponent", "spreadmethod", "startoffset", "stddeviation", "stitchtiles", "stop-color", "stop-opacity", "stroke-dasharray", "stroke-dashoffset", "stroke-linecap", "stroke-linejoin", "stroke-miterlimit", "stroke-opacity", "stroke", "stroke-width", "style", "surfacescale", "systemlanguage", "tabindex", "targetx", "targety", "transform", "transform-origin", "text-anchor", "text-decoration", "text-rendering", "textlength", "type", "u1", "u2", "unicode", "values", "viewbox", "visibility", "version", "vert-adv-y", "vert-origin-x", "vert-origin-y", "width", "word-spacing", "wrap", "writing-mode", "xchannelselector", "ychannelselector", "x", "x1", "x2", "xmlns", "y", "y1", "y2", "z", "zoomandpan"]);
const mathMl = freeze(["accent", "accentunder", "align", "bevelled", "close", "columnsalign", "columnlines", "columnspan", "denomalign", "depth", "dir", "display", "displaystyle", "encoding", "fence", "frame", "height", "href", "id", "largeop", "length", "linethickness", "lspace", "lquote", "mathbackground", "mathcolor", "mathsize", "mathvariant", "maxsize", "minsize", "movablelimits", "notation", "numalign", "open", "rowalign", "rowlines", "rowspacing", "rowspan", "rspace", "rquote", "scriptlevel", "scriptminsize", "scriptsizemultiplier", "selection", "separator", "separators", "stretchy", "subscriptshift", "supscriptshift", "symmetric", "voffset", "width", "xmlns"]);
const xml = freeze(["xlink:href", "xml:id", "xlink:title", "xml:space", "xmlns:xlink"]);
const MUSTACHE_EXPR = seal(/\{\{[\w\W]*|[\w\W]*\}\}/gm);
const ERB_EXPR = seal(/<%[\w\W]*|[\w\W]*%>/gm);
const TMPLIT_EXPR = seal(/\${[\w\W]*}/gm);
const DATA_ATTR = seal(/^data-[\-\w.\u00B7-\uFFFF]/);
const ARIA_ATTR = seal(/^aria-[\-\w]+$/);
const IS_ALLOWED_URI = seal(
  /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|sms|cid|xmpp):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i
  // eslint-disable-line no-useless-escape
);
const IS_SCRIPT_OR_DATA = seal(/^(?:\w+script|data):/i);
const ATTR_WHITESPACE = seal(
  /[\u0000-\u0020\u00A0\u1680\u180E\u2000-\u2029\u205F\u3000]/g
  // eslint-disable-line no-control-regex
);
const DOCTYPE_NAME = seal(/^html$/i);
const CUSTOM_ELEMENT = seal(/^[a-z][.\w]*(-[.\w]+)+$/i);
var EXPRESSIONS = /* @__PURE__ */ Object.freeze({
  __proto__: null,
  MUSTACHE_EXPR,
  ERB_EXPR,
  TMPLIT_EXPR,
  DATA_ATTR,
  ARIA_ATTR,
  IS_ALLOWED_URI,
  IS_SCRIPT_OR_DATA,
  ATTR_WHITESPACE,
  DOCTYPE_NAME,
  CUSTOM_ELEMENT
});
const getGlobal = function getGlobal2() {
  return typeof window === "undefined" ? null : window;
};
const _createTrustedTypesPolicy = function _createTrustedTypesPolicy2(trustedTypes, purifyHostElement) {
  if (typeof trustedTypes !== "object" || typeof trustedTypes.createPolicy !== "function") {
    return null;
  }
  let suffix = null;
  const ATTR_NAME = "data-tt-policy-suffix";
  if (purifyHostElement && purifyHostElement.hasAttribute(ATTR_NAME)) {
    suffix = purifyHostElement.getAttribute(ATTR_NAME);
  }
  const policyName = "dompurify" + (suffix ? "#" + suffix : "");
  try {
    return trustedTypes.createPolicy(policyName, {
      createHTML(html2) {
        return html2;
      },
      createScriptURL(scriptUrl) {
        return scriptUrl;
      }
    });
  } catch (_) {
    console.warn("TrustedTypes policy " + policyName + " could not be created.");
    return null;
  }
};
function createDOMPurify() {
  let window2 = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : getGlobal();
  const DOMPurify = (root) => createDOMPurify(root);
  DOMPurify.version = "3.1.2";
  DOMPurify.removed = [];
  if (!window2 || !window2.document || window2.document.nodeType !== 9) {
    DOMPurify.isSupported = false;
    return DOMPurify;
  }
  let {
    document: document2
  } = window2;
  const originalDocument = document2;
  const currentScript = originalDocument.currentScript;
  const {
    DocumentFragment,
    HTMLTemplateElement,
    Node,
    Element,
    NodeFilter,
    NamedNodeMap = window2.NamedNodeMap || window2.MozNamedAttrMap,
    HTMLFormElement,
    DOMParser,
    trustedTypes
  } = window2;
  const ElementPrototype = Element.prototype;
  const cloneNode = lookupGetter(ElementPrototype, "cloneNode");
  const getNextSibling = lookupGetter(ElementPrototype, "nextSibling");
  const getChildNodes = lookupGetter(ElementPrototype, "childNodes");
  const getParentNode = lookupGetter(ElementPrototype, "parentNode");
  if (typeof HTMLTemplateElement === "function") {
    const template = document2.createElement("template");
    if (template.content && template.content.ownerDocument) {
      document2 = template.content.ownerDocument;
    }
  }
  let trustedTypesPolicy;
  let emptyHTML = "";
  const {
    implementation,
    createNodeIterator,
    createDocumentFragment,
    getElementsByTagName
  } = document2;
  const {
    importNode
  } = originalDocument;
  let hooks = {};
  DOMPurify.isSupported = typeof entries === "function" && typeof getParentNode === "function" && implementation && implementation.createHTMLDocument !== void 0;
  const {
    MUSTACHE_EXPR: MUSTACHE_EXPR2,
    ERB_EXPR: ERB_EXPR2,
    TMPLIT_EXPR: TMPLIT_EXPR2,
    DATA_ATTR: DATA_ATTR2,
    ARIA_ATTR: ARIA_ATTR2,
    IS_SCRIPT_OR_DATA: IS_SCRIPT_OR_DATA2,
    ATTR_WHITESPACE: ATTR_WHITESPACE2,
    CUSTOM_ELEMENT: CUSTOM_ELEMENT2
  } = EXPRESSIONS;
  let {
    IS_ALLOWED_URI: IS_ALLOWED_URI$1
  } = EXPRESSIONS;
  let ALLOWED_TAGS = null;
  const DEFAULT_ALLOWED_TAGS = addToSet({}, [...html$1, ...svg$1, ...svgFilters, ...mathMl$1, ...text]);
  let ALLOWED_ATTR = null;
  const DEFAULT_ALLOWED_ATTR = addToSet({}, [...html, ...svg, ...mathMl, ...xml]);
  let CUSTOM_ELEMENT_HANDLING = Object.seal(create(null, {
    tagNameCheck: {
      writable: true,
      configurable: false,
      enumerable: true,
      value: null
    },
    attributeNameCheck: {
      writable: true,
      configurable: false,
      enumerable: true,
      value: null
    },
    allowCustomizedBuiltInElements: {
      writable: true,
      configurable: false,
      enumerable: true,
      value: false
    }
  }));
  let FORBID_TAGS = null;
  let FORBID_ATTR = null;
  let ALLOW_ARIA_ATTR = true;
  let ALLOW_DATA_ATTR = true;
  let ALLOW_UNKNOWN_PROTOCOLS = false;
  let ALLOW_SELF_CLOSE_IN_ATTR = true;
  let SAFE_FOR_TEMPLATES = false;
  let SAFE_FOR_XML = true;
  let WHOLE_DOCUMENT = false;
  let SET_CONFIG = false;
  let FORCE_BODY = false;
  let RETURN_DOM = false;
  let RETURN_DOM_FRAGMENT = false;
  let RETURN_TRUSTED_TYPE = false;
  let SANITIZE_DOM = true;
  let SANITIZE_NAMED_PROPS = false;
  const SANITIZE_NAMED_PROPS_PREFIX = "user-content-";
  let KEEP_CONTENT = true;
  let IN_PLACE = false;
  let USE_PROFILES = {};
  let FORBID_CONTENTS = null;
  const DEFAULT_FORBID_CONTENTS = addToSet({}, ["annotation-xml", "audio", "colgroup", "desc", "foreignobject", "head", "iframe", "math", "mi", "mn", "mo", "ms", "mtext", "noembed", "noframes", "noscript", "plaintext", "script", "style", "svg", "template", "thead", "title", "video", "xmp"]);
  let DATA_URI_TAGS = null;
  const DEFAULT_DATA_URI_TAGS = addToSet({}, ["audio", "video", "img", "source", "image", "track"]);
  let URI_SAFE_ATTRIBUTES = null;
  const DEFAULT_URI_SAFE_ATTRIBUTES = addToSet({}, ["alt", "class", "for", "id", "label", "name", "pattern", "placeholder", "role", "summary", "title", "value", "style", "xmlns"]);
  const MATHML_NAMESPACE = "http://www.w3.org/1998/Math/MathML";
  const SVG_NAMESPACE = "http://www.w3.org/2000/svg";
  const HTML_NAMESPACE = "http://www.w3.org/1999/xhtml";
  let NAMESPACE = HTML_NAMESPACE;
  let IS_EMPTY_INPUT = false;
  let ALLOWED_NAMESPACES = null;
  const DEFAULT_ALLOWED_NAMESPACES = addToSet({}, [MATHML_NAMESPACE, SVG_NAMESPACE, HTML_NAMESPACE], stringToString);
  let PARSER_MEDIA_TYPE = null;
  const SUPPORTED_PARSER_MEDIA_TYPES = ["application/xhtml+xml", "text/html"];
  const DEFAULT_PARSER_MEDIA_TYPE = "text/html";
  let transformCaseFunc = null;
  let CONFIG = null;
  const MAX_NESTING_DEPTH = 255;
  const formElement = document2.createElement("form");
  const isRegexOrFunction = function isRegexOrFunction2(testValue) {
    return testValue instanceof RegExp || testValue instanceof Function;
  };
  const _parseConfig = function _parseConfig2() {
    let cfg = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : {};
    if (CONFIG && CONFIG === cfg) {
      return;
    }
    if (!cfg || typeof cfg !== "object") {
      cfg = {};
    }
    cfg = clone(cfg);
    PARSER_MEDIA_TYPE = // eslint-disable-next-line unicorn/prefer-includes
    SUPPORTED_PARSER_MEDIA_TYPES.indexOf(cfg.PARSER_MEDIA_TYPE) === -1 ? DEFAULT_PARSER_MEDIA_TYPE : cfg.PARSER_MEDIA_TYPE;
    transformCaseFunc = PARSER_MEDIA_TYPE === "application/xhtml+xml" ? stringToString : stringToLowerCase;
    ALLOWED_TAGS = objectHasOwnProperty(cfg, "ALLOWED_TAGS") ? addToSet({}, cfg.ALLOWED_TAGS, transformCaseFunc) : DEFAULT_ALLOWED_TAGS;
    ALLOWED_ATTR = objectHasOwnProperty(cfg, "ALLOWED_ATTR") ? addToSet({}, cfg.ALLOWED_ATTR, transformCaseFunc) : DEFAULT_ALLOWED_ATTR;
    ALLOWED_NAMESPACES = objectHasOwnProperty(cfg, "ALLOWED_NAMESPACES") ? addToSet({}, cfg.ALLOWED_NAMESPACES, stringToString) : DEFAULT_ALLOWED_NAMESPACES;
    URI_SAFE_ATTRIBUTES = objectHasOwnProperty(cfg, "ADD_URI_SAFE_ATTR") ? addToSet(
      clone(DEFAULT_URI_SAFE_ATTRIBUTES),
      // eslint-disable-line indent
      cfg.ADD_URI_SAFE_ATTR,
      // eslint-disable-line indent
      transformCaseFunc
      // eslint-disable-line indent
    ) : DEFAULT_URI_SAFE_ATTRIBUTES;
    DATA_URI_TAGS = objectHasOwnProperty(cfg, "ADD_DATA_URI_TAGS") ? addToSet(
      clone(DEFAULT_DATA_URI_TAGS),
      // eslint-disable-line indent
      cfg.ADD_DATA_URI_TAGS,
      // eslint-disable-line indent
      transformCaseFunc
      // eslint-disable-line indent
    ) : DEFAULT_DATA_URI_TAGS;
    FORBID_CONTENTS = objectHasOwnProperty(cfg, "FORBID_CONTENTS") ? addToSet({}, cfg.FORBID_CONTENTS, transformCaseFunc) : DEFAULT_FORBID_CONTENTS;
    FORBID_TAGS = objectHasOwnProperty(cfg, "FORBID_TAGS") ? addToSet({}, cfg.FORBID_TAGS, transformCaseFunc) : {};
    FORBID_ATTR = objectHasOwnProperty(cfg, "FORBID_ATTR") ? addToSet({}, cfg.FORBID_ATTR, transformCaseFunc) : {};
    USE_PROFILES = objectHasOwnProperty(cfg, "USE_PROFILES") ? cfg.USE_PROFILES : false;
    ALLOW_ARIA_ATTR = cfg.ALLOW_ARIA_ATTR !== false;
    ALLOW_DATA_ATTR = cfg.ALLOW_DATA_ATTR !== false;
    ALLOW_UNKNOWN_PROTOCOLS = cfg.ALLOW_UNKNOWN_PROTOCOLS || false;
    ALLOW_SELF_CLOSE_IN_ATTR = cfg.ALLOW_SELF_CLOSE_IN_ATTR !== false;
    SAFE_FOR_TEMPLATES = cfg.SAFE_FOR_TEMPLATES || false;
    SAFE_FOR_XML = cfg.SAFE_FOR_XML !== false;
    WHOLE_DOCUMENT = cfg.WHOLE_DOCUMENT || false;
    RETURN_DOM = cfg.RETURN_DOM || false;
    RETURN_DOM_FRAGMENT = cfg.RETURN_DOM_FRAGMENT || false;
    RETURN_TRUSTED_TYPE = cfg.RETURN_TRUSTED_TYPE || false;
    FORCE_BODY = cfg.FORCE_BODY || false;
    SANITIZE_DOM = cfg.SANITIZE_DOM !== false;
    SANITIZE_NAMED_PROPS = cfg.SANITIZE_NAMED_PROPS || false;
    KEEP_CONTENT = cfg.KEEP_CONTENT !== false;
    IN_PLACE = cfg.IN_PLACE || false;
    IS_ALLOWED_URI$1 = cfg.ALLOWED_URI_REGEXP || IS_ALLOWED_URI;
    NAMESPACE = cfg.NAMESPACE || HTML_NAMESPACE;
    CUSTOM_ELEMENT_HANDLING = cfg.CUSTOM_ELEMENT_HANDLING || {};
    if (cfg.CUSTOM_ELEMENT_HANDLING && isRegexOrFunction(cfg.CUSTOM_ELEMENT_HANDLING.tagNameCheck)) {
      CUSTOM_ELEMENT_HANDLING.tagNameCheck = cfg.CUSTOM_ELEMENT_HANDLING.tagNameCheck;
    }
    if (cfg.CUSTOM_ELEMENT_HANDLING && isRegexOrFunction(cfg.CUSTOM_ELEMENT_HANDLING.attributeNameCheck)) {
      CUSTOM_ELEMENT_HANDLING.attributeNameCheck = cfg.CUSTOM_ELEMENT_HANDLING.attributeNameCheck;
    }
    if (cfg.CUSTOM_ELEMENT_HANDLING && typeof cfg.CUSTOM_ELEMENT_HANDLING.allowCustomizedBuiltInElements === "boolean") {
      CUSTOM_ELEMENT_HANDLING.allowCustomizedBuiltInElements = cfg.CUSTOM_ELEMENT_HANDLING.allowCustomizedBuiltInElements;
    }
    if (SAFE_FOR_TEMPLATES) {
      ALLOW_DATA_ATTR = false;
    }
    if (RETURN_DOM_FRAGMENT) {
      RETURN_DOM = true;
    }
    if (USE_PROFILES) {
      ALLOWED_TAGS = addToSet({}, text);
      ALLOWED_ATTR = [];
      if (USE_PROFILES.html === true) {
        addToSet(ALLOWED_TAGS, html$1);
        addToSet(ALLOWED_ATTR, html);
      }
      if (USE_PROFILES.svg === true) {
        addToSet(ALLOWED_TAGS, svg$1);
        addToSet(ALLOWED_ATTR, svg);
        addToSet(ALLOWED_ATTR, xml);
      }
      if (USE_PROFILES.svgFilters === true) {
        addToSet(ALLOWED_TAGS, svgFilters);
        addToSet(ALLOWED_ATTR, svg);
        addToSet(ALLOWED_ATTR, xml);
      }
      if (USE_PROFILES.mathMl === true) {
        addToSet(ALLOWED_TAGS, mathMl$1);
        addToSet(ALLOWED_ATTR, mathMl);
        addToSet(ALLOWED_ATTR, xml);
      }
    }
    if (cfg.ADD_TAGS) {
      if (ALLOWED_TAGS === DEFAULT_ALLOWED_TAGS) {
        ALLOWED_TAGS = clone(ALLOWED_TAGS);
      }
      addToSet(ALLOWED_TAGS, cfg.ADD_TAGS, transformCaseFunc);
    }
    if (cfg.ADD_ATTR) {
      if (ALLOWED_ATTR === DEFAULT_ALLOWED_ATTR) {
        ALLOWED_ATTR = clone(ALLOWED_ATTR);
      }
      addToSet(ALLOWED_ATTR, cfg.ADD_ATTR, transformCaseFunc);
    }
    if (cfg.ADD_URI_SAFE_ATTR) {
      addToSet(URI_SAFE_ATTRIBUTES, cfg.ADD_URI_SAFE_ATTR, transformCaseFunc);
    }
    if (cfg.FORBID_CONTENTS) {
      if (FORBID_CONTENTS === DEFAULT_FORBID_CONTENTS) {
        FORBID_CONTENTS = clone(FORBID_CONTENTS);
      }
      addToSet(FORBID_CONTENTS, cfg.FORBID_CONTENTS, transformCaseFunc);
    }
    if (KEEP_CONTENT) {
      ALLOWED_TAGS["#text"] = true;
    }
    if (WHOLE_DOCUMENT) {
      addToSet(ALLOWED_TAGS, ["html", "head", "body"]);
    }
    if (ALLOWED_TAGS.table) {
      addToSet(ALLOWED_TAGS, ["tbody"]);
      delete FORBID_TAGS.tbody;
    }
    if (cfg.TRUSTED_TYPES_POLICY) {
      if (typeof cfg.TRUSTED_TYPES_POLICY.createHTML !== "function") {
        throw typeErrorCreate('TRUSTED_TYPES_POLICY configuration option must provide a "createHTML" hook.');
      }
      if (typeof cfg.TRUSTED_TYPES_POLICY.createScriptURL !== "function") {
        throw typeErrorCreate('TRUSTED_TYPES_POLICY configuration option must provide a "createScriptURL" hook.');
      }
      trustedTypesPolicy = cfg.TRUSTED_TYPES_POLICY;
      emptyHTML = trustedTypesPolicy.createHTML("");
    } else {
      if (trustedTypesPolicy === void 0) {
        trustedTypesPolicy = _createTrustedTypesPolicy(trustedTypes, currentScript);
      }
      if (trustedTypesPolicy !== null && typeof emptyHTML === "string") {
        emptyHTML = trustedTypesPolicy.createHTML("");
      }
    }
    if (freeze) {
      freeze(cfg);
    }
    CONFIG = cfg;
  };
  const MATHML_TEXT_INTEGRATION_POINTS = addToSet({}, ["mi", "mo", "mn", "ms", "mtext"]);
  const HTML_INTEGRATION_POINTS = addToSet({}, ["foreignobject", "annotation-xml"]);
  const COMMON_SVG_AND_HTML_ELEMENTS = addToSet({}, ["title", "style", "font", "a", "script"]);
  const ALL_SVG_TAGS = addToSet({}, [...svg$1, ...svgFilters, ...svgDisallowed]);
  const ALL_MATHML_TAGS = addToSet({}, [...mathMl$1, ...mathMlDisallowed]);
  const _checkValidNamespace = function _checkValidNamespace2(element) {
    let parent = getParentNode(element);
    if (!parent || !parent.tagName) {
      parent = {
        namespaceURI: NAMESPACE,
        tagName: "template"
      };
    }
    const tagName = stringToLowerCase(element.tagName);
    const parentTagName = stringToLowerCase(parent.tagName);
    if (!ALLOWED_NAMESPACES[element.namespaceURI]) {
      return false;
    }
    if (element.namespaceURI === SVG_NAMESPACE) {
      if (parent.namespaceURI === HTML_NAMESPACE) {
        return tagName === "svg";
      }
      if (parent.namespaceURI === MATHML_NAMESPACE) {
        return tagName === "svg" && (parentTagName === "annotation-xml" || MATHML_TEXT_INTEGRATION_POINTS[parentTagName]);
      }
      return Boolean(ALL_SVG_TAGS[tagName]);
    }
    if (element.namespaceURI === MATHML_NAMESPACE) {
      if (parent.namespaceURI === HTML_NAMESPACE) {
        return tagName === "math";
      }
      if (parent.namespaceURI === SVG_NAMESPACE) {
        return tagName === "math" && HTML_INTEGRATION_POINTS[parentTagName];
      }
      return Boolean(ALL_MATHML_TAGS[tagName]);
    }
    if (element.namespaceURI === HTML_NAMESPACE) {
      if (parent.namespaceURI === SVG_NAMESPACE && !HTML_INTEGRATION_POINTS[parentTagName]) {
        return false;
      }
      if (parent.namespaceURI === MATHML_NAMESPACE && !MATHML_TEXT_INTEGRATION_POINTS[parentTagName]) {
        return false;
      }
      return !ALL_MATHML_TAGS[tagName] && (COMMON_SVG_AND_HTML_ELEMENTS[tagName] || !ALL_SVG_TAGS[tagName]);
    }
    if (PARSER_MEDIA_TYPE === "application/xhtml+xml" && ALLOWED_NAMESPACES[element.namespaceURI]) {
      return true;
    }
    return false;
  };
  const _forceRemove = function _forceRemove2(node) {
    arrayPush(DOMPurify.removed, {
      element: node
    });
    try {
      node.parentNode.removeChild(node);
    } catch (_) {
      node.remove();
    }
  };
  const _removeAttribute = function _removeAttribute2(name, node) {
    try {
      arrayPush(DOMPurify.removed, {
        attribute: node.getAttributeNode(name),
        from: node
      });
    } catch (_) {
      arrayPush(DOMPurify.removed, {
        attribute: null,
        from: node
      });
    }
    node.removeAttribute(name);
    if (name === "is" && !ALLOWED_ATTR[name]) {
      if (RETURN_DOM || RETURN_DOM_FRAGMENT) {
        try {
          _forceRemove(node);
        } catch (_) {
        }
      } else {
        try {
          node.setAttribute(name, "");
        } catch (_) {
        }
      }
    }
  };
  const _initDocument = function _initDocument2(dirty) {
    let doc = null;
    let leadingWhitespace = null;
    if (FORCE_BODY) {
      dirty = "<remove></remove>" + dirty;
    } else {
      const matches = stringMatch(dirty, /^[\r\n\t ]+/);
      leadingWhitespace = matches && matches[0];
    }
    if (PARSER_MEDIA_TYPE === "application/xhtml+xml" && NAMESPACE === HTML_NAMESPACE) {
      dirty = '<html xmlns="http://www.w3.org/1999/xhtml"><head></head><body>' + dirty + "</body></html>";
    }
    const dirtyPayload = trustedTypesPolicy ? trustedTypesPolicy.createHTML(dirty) : dirty;
    if (NAMESPACE === HTML_NAMESPACE) {
      try {
        doc = new DOMParser().parseFromString(dirtyPayload, PARSER_MEDIA_TYPE);
      } catch (_) {
      }
    }
    if (!doc || !doc.documentElement) {
      doc = implementation.createDocument(NAMESPACE, "template", null);
      try {
        doc.documentElement.innerHTML = IS_EMPTY_INPUT ? emptyHTML : dirtyPayload;
      } catch (_) {
      }
    }
    const body = doc.body || doc.documentElement;
    if (dirty && leadingWhitespace) {
      body.insertBefore(document2.createTextNode(leadingWhitespace), body.childNodes[0] || null);
    }
    if (NAMESPACE === HTML_NAMESPACE) {
      return getElementsByTagName.call(doc, WHOLE_DOCUMENT ? "html" : "body")[0];
    }
    return WHOLE_DOCUMENT ? doc.documentElement : body;
  };
  const _createNodeIterator = function _createNodeIterator2(root) {
    return createNodeIterator.call(
      root.ownerDocument || root,
      root,
      // eslint-disable-next-line no-bitwise
      NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_COMMENT | NodeFilter.SHOW_TEXT | NodeFilter.SHOW_PROCESSING_INSTRUCTION | NodeFilter.SHOW_CDATA_SECTION,
      null
    );
  };
  const _isClobbered = function _isClobbered2(elm) {
    return elm instanceof HTMLFormElement && // eslint-disable-next-line unicorn/no-typeof-undefined
    (typeof elm.__depth !== "undefined" && typeof elm.__depth !== "number" || // eslint-disable-next-line unicorn/no-typeof-undefined
    typeof elm.__removalCount !== "undefined" && typeof elm.__removalCount !== "number" || typeof elm.nodeName !== "string" || typeof elm.textContent !== "string" || typeof elm.removeChild !== "function" || !(elm.attributes instanceof NamedNodeMap) || typeof elm.removeAttribute !== "function" || typeof elm.setAttribute !== "function" || typeof elm.namespaceURI !== "string" || typeof elm.insertBefore !== "function" || typeof elm.hasChildNodes !== "function");
  };
  const _isNode = function _isNode2(object) {
    return typeof Node === "function" && object instanceof Node;
  };
  const _executeHook = function _executeHook2(entryPoint, currentNode, data) {
    if (!hooks[entryPoint]) {
      return;
    }
    arrayForEach(hooks[entryPoint], (hook) => {
      hook.call(DOMPurify, currentNode, data, CONFIG);
    });
  };
  const _sanitizeElements = function _sanitizeElements2(currentNode) {
    let content = null;
    _executeHook("beforeSanitizeElements", currentNode, null);
    if (_isClobbered(currentNode)) {
      _forceRemove(currentNode);
      return true;
    }
    const tagName = transformCaseFunc(currentNode.nodeName);
    _executeHook("uponSanitizeElement", currentNode, {
      tagName,
      allowedTags: ALLOWED_TAGS
    });
    if (currentNode.hasChildNodes() && !_isNode(currentNode.firstElementChild) && regExpTest(/<[/\w]/g, currentNode.innerHTML) && regExpTest(/<[/\w]/g, currentNode.textContent)) {
      _forceRemove(currentNode);
      return true;
    }
    if (currentNode.nodeType === 7) {
      _forceRemove(currentNode);
      return true;
    }
    if (SAFE_FOR_XML && currentNode.nodeType === 8 && regExpTest(/<[/\w]/g, currentNode.data)) {
      _forceRemove(currentNode);
      return true;
    }
    if (!ALLOWED_TAGS[tagName] || FORBID_TAGS[tagName]) {
      if (!FORBID_TAGS[tagName] && _isBasicCustomElement(tagName)) {
        if (CUSTOM_ELEMENT_HANDLING.tagNameCheck instanceof RegExp && regExpTest(CUSTOM_ELEMENT_HANDLING.tagNameCheck, tagName)) {
          return false;
        }
        if (CUSTOM_ELEMENT_HANDLING.tagNameCheck instanceof Function && CUSTOM_ELEMENT_HANDLING.tagNameCheck(tagName)) {
          return false;
        }
      }
      if (KEEP_CONTENT && !FORBID_CONTENTS[tagName]) {
        const parentNode = getParentNode(currentNode) || currentNode.parentNode;
        const childNodes = getChildNodes(currentNode) || currentNode.childNodes;
        if (childNodes && parentNode) {
          const childCount = childNodes.length;
          for (let i = childCount - 1; i >= 0; --i) {
            const childClone = cloneNode(childNodes[i], true);
            childClone.__removalCount = (currentNode.__removalCount || 0) + 1;
            parentNode.insertBefore(childClone, getNextSibling(currentNode));
          }
        }
      }
      _forceRemove(currentNode);
      return true;
    }
    if (currentNode instanceof Element && !_checkValidNamespace(currentNode)) {
      _forceRemove(currentNode);
      return true;
    }
    if ((tagName === "noscript" || tagName === "noembed" || tagName === "noframes") && regExpTest(/<\/no(script|embed|frames)/i, currentNode.innerHTML)) {
      _forceRemove(currentNode);
      return true;
    }
    if (SAFE_FOR_TEMPLATES && currentNode.nodeType === 3) {
      content = currentNode.textContent;
      arrayForEach([MUSTACHE_EXPR2, ERB_EXPR2, TMPLIT_EXPR2], (expr) => {
        content = stringReplace(content, expr, " ");
      });
      if (currentNode.textContent !== content) {
        arrayPush(DOMPurify.removed, {
          element: currentNode.cloneNode()
        });
        currentNode.textContent = content;
      }
    }
    _executeHook("afterSanitizeElements", currentNode, null);
    return false;
  };
  const _isValidAttribute = function _isValidAttribute2(lcTag, lcName, value) {
    if (SANITIZE_DOM && (lcName === "id" || lcName === "name") && (value in document2 || value in formElement)) {
      return false;
    }
    if (ALLOW_DATA_ATTR && !FORBID_ATTR[lcName] && regExpTest(DATA_ATTR2, lcName))
      ;
    else if (ALLOW_ARIA_ATTR && regExpTest(ARIA_ATTR2, lcName))
      ;
    else if (!ALLOWED_ATTR[lcName] || FORBID_ATTR[lcName]) {
      if (
        // First condition does a very basic check if a) it's basically a valid custom element tagname AND
        // b) if the tagName passes whatever the user has configured for CUSTOM_ELEMENT_HANDLING.tagNameCheck
        // and c) if the attribute name passes whatever the user has configured for CUSTOM_ELEMENT_HANDLING.attributeNameCheck
        _isBasicCustomElement(lcTag) && (CUSTOM_ELEMENT_HANDLING.tagNameCheck instanceof RegExp && regExpTest(CUSTOM_ELEMENT_HANDLING.tagNameCheck, lcTag) || CUSTOM_ELEMENT_HANDLING.tagNameCheck instanceof Function && CUSTOM_ELEMENT_HANDLING.tagNameCheck(lcTag)) && (CUSTOM_ELEMENT_HANDLING.attributeNameCheck instanceof RegExp && regExpTest(CUSTOM_ELEMENT_HANDLING.attributeNameCheck, lcName) || CUSTOM_ELEMENT_HANDLING.attributeNameCheck instanceof Function && CUSTOM_ELEMENT_HANDLING.attributeNameCheck(lcName)) || // Alternative, second condition checks if it's an `is`-attribute, AND
        // the value passes whatever the user has configured for CUSTOM_ELEMENT_HANDLING.tagNameCheck
        lcName === "is" && CUSTOM_ELEMENT_HANDLING.allowCustomizedBuiltInElements && (CUSTOM_ELEMENT_HANDLING.tagNameCheck instanceof RegExp && regExpTest(CUSTOM_ELEMENT_HANDLING.tagNameCheck, value) || CUSTOM_ELEMENT_HANDLING.tagNameCheck instanceof Function && CUSTOM_ELEMENT_HANDLING.tagNameCheck(value))
      )
        ;
      else {
        return false;
      }
    } else if (URI_SAFE_ATTRIBUTES[lcName])
      ;
    else if (regExpTest(IS_ALLOWED_URI$1, stringReplace(value, ATTR_WHITESPACE2, "")))
      ;
    else if ((lcName === "src" || lcName === "xlink:href" || lcName === "href") && lcTag !== "script" && stringIndexOf(value, "data:") === 0 && DATA_URI_TAGS[lcTag])
      ;
    else if (ALLOW_UNKNOWN_PROTOCOLS && !regExpTest(IS_SCRIPT_OR_DATA2, stringReplace(value, ATTR_WHITESPACE2, "")))
      ;
    else if (value) {
      return false;
    } else
      ;
    return true;
  };
  const _isBasicCustomElement = function _isBasicCustomElement2(tagName) {
    return tagName !== "annotation-xml" && stringMatch(tagName, CUSTOM_ELEMENT2);
  };
  const _sanitizeAttributes = function _sanitizeAttributes2(currentNode) {
    _executeHook("beforeSanitizeAttributes", currentNode, null);
    const {
      attributes
    } = currentNode;
    if (!attributes) {
      return;
    }
    const hookEvent = {
      attrName: "",
      attrValue: "",
      keepAttr: true,
      allowedAttributes: ALLOWED_ATTR
    };
    let l = attributes.length;
    while (l--) {
      const attr = attributes[l];
      const {
        name,
        namespaceURI,
        value: attrValue
      } = attr;
      const lcName = transformCaseFunc(name);
      let value = name === "value" ? attrValue : stringTrim(attrValue);
      hookEvent.attrName = lcName;
      hookEvent.attrValue = value;
      hookEvent.keepAttr = true;
      hookEvent.forceKeepAttr = void 0;
      _executeHook("uponSanitizeAttribute", currentNode, hookEvent);
      value = hookEvent.attrValue;
      if (hookEvent.forceKeepAttr) {
        continue;
      }
      _removeAttribute(name, currentNode);
      if (!hookEvent.keepAttr) {
        continue;
      }
      if (!ALLOW_SELF_CLOSE_IN_ATTR && regExpTest(/\/>/i, value)) {
        _removeAttribute(name, currentNode);
        continue;
      }
      if (SAFE_FOR_TEMPLATES) {
        arrayForEach([MUSTACHE_EXPR2, ERB_EXPR2, TMPLIT_EXPR2], (expr) => {
          value = stringReplace(value, expr, " ");
        });
      }
      const lcTag = transformCaseFunc(currentNode.nodeName);
      if (!_isValidAttribute(lcTag, lcName, value)) {
        continue;
      }
      if (SANITIZE_NAMED_PROPS && (lcName === "id" || lcName === "name")) {
        _removeAttribute(name, currentNode);
        value = SANITIZE_NAMED_PROPS_PREFIX + value;
      }
      if (trustedTypesPolicy && typeof trustedTypes === "object" && typeof trustedTypes.getAttributeType === "function") {
        if (namespaceURI)
          ;
        else {
          switch (trustedTypes.getAttributeType(lcTag, lcName)) {
            case "TrustedHTML": {
              value = trustedTypesPolicy.createHTML(value);
              break;
            }
            case "TrustedScriptURL": {
              value = trustedTypesPolicy.createScriptURL(value);
              break;
            }
          }
        }
      }
      try {
        if (namespaceURI) {
          currentNode.setAttributeNS(namespaceURI, name, value);
        } else {
          currentNode.setAttribute(name, value);
        }
        arrayPop(DOMPurify.removed);
      } catch (_) {
      }
    }
    _executeHook("afterSanitizeAttributes", currentNode, null);
  };
  const _sanitizeShadowDOM = function _sanitizeShadowDOM2(fragment) {
    let shadowNode = null;
    const shadowIterator = _createNodeIterator(fragment);
    _executeHook("beforeSanitizeShadowDOM", fragment, null);
    while (shadowNode = shadowIterator.nextNode()) {
      _executeHook("uponSanitizeShadowNode", shadowNode, null);
      if (_sanitizeElements(shadowNode)) {
        continue;
      }
      const parentNode = getParentNode(shadowNode);
      if (shadowNode.nodeType === 1) {
        if (parentNode && parentNode.__depth) {
          shadowNode.__depth = (shadowNode.__removalCount || 0) + parentNode.__depth + 1;
        } else {
          shadowNode.__depth = 1;
        }
      }
      if (shadowNode.__depth >= MAX_NESTING_DEPTH) {
        _forceRemove(shadowNode);
      }
      if (shadowNode.content instanceof DocumentFragment) {
        shadowNode.content.__depth = shadowNode.__depth;
        _sanitizeShadowDOM2(shadowNode.content);
      }
      _sanitizeAttributes(shadowNode);
    }
    _executeHook("afterSanitizeShadowDOM", fragment, null);
  };
  DOMPurify.sanitize = function(dirty) {
    let cfg = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : {};
    let body = null;
    let importedNode = null;
    let currentNode = null;
    let returnNode = null;
    IS_EMPTY_INPUT = !dirty;
    if (IS_EMPTY_INPUT) {
      dirty = "<!-->";
    }
    if (typeof dirty !== "string" && !_isNode(dirty)) {
      if (typeof dirty.toString === "function") {
        dirty = dirty.toString();
        if (typeof dirty !== "string") {
          throw typeErrorCreate("dirty is not a string, aborting");
        }
      } else {
        throw typeErrorCreate("toString is not a function");
      }
    }
    if (!DOMPurify.isSupported) {
      return dirty;
    }
    if (!SET_CONFIG) {
      _parseConfig(cfg);
    }
    DOMPurify.removed = [];
    if (typeof dirty === "string") {
      IN_PLACE = false;
    }
    if (IN_PLACE) {
      if (dirty.nodeName) {
        const tagName = transformCaseFunc(dirty.nodeName);
        if (!ALLOWED_TAGS[tagName] || FORBID_TAGS[tagName]) {
          throw typeErrorCreate("root node is forbidden and cannot be sanitized in-place");
        }
      }
    } else if (dirty instanceof Node) {
      body = _initDocument("<!---->");
      importedNode = body.ownerDocument.importNode(dirty, true);
      if (importedNode.nodeType === 1 && importedNode.nodeName === "BODY") {
        body = importedNode;
      } else if (importedNode.nodeName === "HTML") {
        body = importedNode;
      } else {
        body.appendChild(importedNode);
      }
    } else {
      if (!RETURN_DOM && !SAFE_FOR_TEMPLATES && !WHOLE_DOCUMENT && // eslint-disable-next-line unicorn/prefer-includes
      dirty.indexOf("<") === -1) {
        return trustedTypesPolicy && RETURN_TRUSTED_TYPE ? trustedTypesPolicy.createHTML(dirty) : dirty;
      }
      body = _initDocument(dirty);
      if (!body) {
        return RETURN_DOM ? null : RETURN_TRUSTED_TYPE ? emptyHTML : "";
      }
    }
    if (body && FORCE_BODY) {
      _forceRemove(body.firstChild);
    }
    const nodeIterator = _createNodeIterator(IN_PLACE ? dirty : body);
    while (currentNode = nodeIterator.nextNode()) {
      if (_sanitizeElements(currentNode)) {
        continue;
      }
      const parentNode = getParentNode(currentNode);
      if (currentNode.nodeType === 1) {
        if (parentNode && parentNode.__depth) {
          currentNode.__depth = (currentNode.__removalCount || 0) + parentNode.__depth + 1;
        } else {
          currentNode.__depth = 1;
        }
      }
      if (currentNode.__depth >= MAX_NESTING_DEPTH) {
        _forceRemove(currentNode);
      }
      if (currentNode.content instanceof DocumentFragment) {
        currentNode.content.__depth = currentNode.__depth;
        _sanitizeShadowDOM(currentNode.content);
      }
      _sanitizeAttributes(currentNode);
    }
    if (IN_PLACE) {
      return dirty;
    }
    if (RETURN_DOM) {
      if (RETURN_DOM_FRAGMENT) {
        returnNode = createDocumentFragment.call(body.ownerDocument);
        while (body.firstChild) {
          returnNode.appendChild(body.firstChild);
        }
      } else {
        returnNode = body;
      }
      if (ALLOWED_ATTR.shadowroot || ALLOWED_ATTR.shadowrootmode) {
        returnNode = importNode.call(originalDocument, returnNode, true);
      }
      return returnNode;
    }
    let serializedHTML = WHOLE_DOCUMENT ? body.outerHTML : body.innerHTML;
    if (WHOLE_DOCUMENT && ALLOWED_TAGS["!doctype"] && body.ownerDocument && body.ownerDocument.doctype && body.ownerDocument.doctype.name && regExpTest(DOCTYPE_NAME, body.ownerDocument.doctype.name)) {
      serializedHTML = "<!DOCTYPE " + body.ownerDocument.doctype.name + ">\n" + serializedHTML;
    }
    if (SAFE_FOR_TEMPLATES) {
      arrayForEach([MUSTACHE_EXPR2, ERB_EXPR2, TMPLIT_EXPR2], (expr) => {
        serializedHTML = stringReplace(serializedHTML, expr, " ");
      });
    }
    return trustedTypesPolicy && RETURN_TRUSTED_TYPE ? trustedTypesPolicy.createHTML(serializedHTML) : serializedHTML;
  };
  DOMPurify.setConfig = function() {
    let cfg = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : {};
    _parseConfig(cfg);
    SET_CONFIG = true;
  };
  DOMPurify.clearConfig = function() {
    CONFIG = null;
    SET_CONFIG = false;
  };
  DOMPurify.isValidAttribute = function(tag, attr, value) {
    if (!CONFIG) {
      _parseConfig({});
    }
    const lcTag = transformCaseFunc(tag);
    const lcName = transformCaseFunc(attr);
    return _isValidAttribute(lcTag, lcName, value);
  };
  DOMPurify.addHook = function(entryPoint, hookFunction) {
    if (typeof hookFunction !== "function") {
      return;
    }
    hooks[entryPoint] = hooks[entryPoint] || [];
    arrayPush(hooks[entryPoint], hookFunction);
  };
  DOMPurify.removeHook = function(entryPoint) {
    if (hooks[entryPoint]) {
      return arrayPop(hooks[entryPoint]);
    }
  };
  DOMPurify.removeHooks = function(entryPoint) {
    if (hooks[entryPoint]) {
      hooks[entryPoint] = [];
    }
  };
  DOMPurify.removeAllHooks = function() {
    hooks = {};
  };
  return DOMPurify;
}
var purify = createDOMPurify();
const Toast = /* @__PURE__ */ componentQrl(/* @__PURE__ */ inlinedQrl((props) => {
  const mounted = useSignal(false);
  const removed = useSignal(false);
  const swiping = useSignal(false);
  const swipeOut = useSignal(false);
  const offsetBeforeRemove = useSignal(0);
  const initialHeight = useSignal(0);
  const dragStartTime = useSignal(null);
  const toastRef = useSignal();
  const closeTimerStartTimeRef = useSignal(0);
  const lastCloseTimerStartTimeRef = useSignal(0);
  const pointerStartRef = useSignal(null);
  const isDocumentHidden = useSignal(false);
  const isFront = props.index === 0;
  const isVisible = props.index + 1 <= props.visibleToasts;
  const dismissible = props.toast.dismissible !== false;
  const toastClass = props.toast.class ?? "";
  const toastDescriptionClass = props.toast.descriptionClass ?? "";
  const [y, x] = props.position.split("-");
  const invertChecked = isSignal(props.invert) ? props.invert.value : props.invert;
  const invert = useComputedQrl(/* @__PURE__ */ inlinedQrl(() => {
    const [invertChecked2, props2] = useLexicalScope();
    return props2.toast.invert ?? invertChecked2;
  }, "Toast_component_invert_useComputed_qqN0d9btNrI", [
    invertChecked,
    props
  ]));
  const disabled = props.toast.type === "loading";
  const heightIndex = useComputedQrl(/* @__PURE__ */ inlinedQrl(() => {
    const [props2] = useLexicalScope();
    const hSet = Array.from(new Set(props2.heights.value.map((h) => h.toastId)));
    const idx = hSet.findIndex((id) => id === props2.toast.id) || 0;
    return idx === -1 ? 0 : idx;
  }, "Toast_component_heightIndex_useComputed_OIGgvvYWlgA", [
    props
  ]));
  const closeButton = useComputedQrl(/* @__PURE__ */ inlinedQrl(() => {
    const [props2] = useLexicalScope();
    return props2.toast.closeButton ?? props2.closeButton;
  }, "Toast_component_closeButton_useComputed_i5kXixhtrRM", [
    props
  ]));
  const duration = useComputedQrl(/* @__PURE__ */ inlinedQrl(() => {
    const [props2] = useLexicalScope();
    return props2.toast.duration ?? props2.duration ?? TOAST_LIFETIME;
  }, "Toast_component_duration_useComputed_GukVrWhE1m8", [
    props
  ]));
  const toastsHeightBefore = useComputedQrl(/* @__PURE__ */ inlinedQrl(() => {
    const [heightIndex2, props2] = useLexicalScope();
    return props2.heights.value.reduce((prev, curr, reducerIndex) => {
      if (reducerIndex >= heightIndex2.value)
        return prev;
      return prev + curr.height;
    }, 0);
  }, "Toast_component_toastsHeightBefore_useComputed_oFUauznEUQY", [
    heightIndex,
    props
  ]));
  const offset = useComputedQrl(/* @__PURE__ */ inlinedQrl(() => {
    const [heightIndex2, props2, toastsHeightBefore2] = useLexicalScope();
    return heightIndex2.value * props2.gap + toastsHeightBefore2.value;
  }, "Toast_component_offset_useComputed_TZVlEVQ5zFw", [
    heightIndex,
    props,
    toastsHeightBefore
  ]));
  const deleteToast = /* @__PURE__ */ inlinedQrl(() => {
    const [offset2, offsetBeforeRemove2, props2, removed2] = useLexicalScope();
    removed2.value = true;
    offsetBeforeRemove2.value = offset2.value;
    props2.heights.value = props2.heights.value.filter((h) => h.toastId !== props2.toast.id);
    setTimeout(() => {
      props2.removeToast(props2.toast);
    }, TIME_BEFORE_UNMOUNT);
  }, "Toast_component_deleteToast_WysUX9ZGuY4", [
    offset,
    offsetBeforeRemove,
    props,
    removed
  ]);
  function getLoadingIcon() {
    _jsxBranch();
    if (props.icons?.loading)
      return /* @__PURE__ */ _jsxQ("div", null, {
        class: "qwik-loader",
        "data-visible": _fnSignal((p0) => String(p0.toast.type === "loading"), [
          props
        ], 'String(p0.toast.type==="loading")')
      }, _fnSignal((p0) => p0.icons.loading, [
        props
      ], "p0.icons.loading"), 3, "U0_0");
    if (props.loadingIcon)
      return /* @__PURE__ */ _jsxQ("div", null, {
        class: "qwik-loader",
        "data-visible": _fnSignal((p0) => String(p0.toast.type === "loading"), [
          props
        ], 'String(p0.toast.type==="loading")')
      }, _fnSignal((p0) => p0.loadingIcon, [
        props
      ], "p0.loadingIcon"), 3, "U0_1");
    return /* @__PURE__ */ _jsxC(Loader, {
      get visible() {
        return props.toast.type === "loading";
      },
      [_IMMUTABLE]: {
        visible: _fnSignal((p0) => p0.toast.type === "loading", [
          props
        ], 'p0.toast.type==="loading"')
      }
    }, 3, "U0_2");
  }
  function sanitizeHTML(html2) {
    return purify.sanitize(html2);
  }
  useTaskQrl(/* @__PURE__ */ inlinedQrl(({ track }) => {
    const [initialHeight2, props2, toastRef2] = useLexicalScope();
    track(() => toastRef2.value);
    if (!toastRef2.value)
      return;
    const originalHeight = toastRef2.value.style.height;
    toastRef2.value.style.height = "auto";
    const newHeight = toastRef2.value.getBoundingClientRect().height;
    toastRef2.value.style.height = originalHeight;
    initialHeight2.value = newHeight;
    const exists = props2.heights.value.find((h) => h.toastId === props2.toast.id);
    props2.heights.value = exists ? props2.heights.value.map((h) => h.toastId === props2.toast.id ? {
      ...h,
      height: newHeight
    } : h) : [
      {
        toastId: props2.toast.id,
        height: newHeight,
        position: props2.position
      },
      ...props2.heights.value
    ];
  }, "Toast_component_useTask_9CWk06j70CE", [
    initialHeight,
    props,
    toastRef
  ]));
  useTaskQrl(/* @__PURE__ */ inlinedQrl(({ track, cleanup }) => {
    const [closeTimerStartTimeRef2, deleteToast2, duration2, isDocumentHidden2, lastCloseTimerStartTimeRef2, props2] = useLexicalScope();
    track(() => props2.expanded.value);
    track(() => props2.interacting);
    track(() => props2.expandByDefault);
    track(() => props2.toast);
    let remainingTime = track(() => duration2.value);
    track(() => props2.toast.promise);
    track(() => props2.toast.type);
    track(() => props2.pauseWhenPageIsHidden);
    track(() => isDocumentHidden2.value);
    if (props2.toast.promise && props2.toast.type === "loading" || props2.toast.duration === Infinity || props2.toast.type === "loading")
      return;
    let timeoutId;
    const pauseTimer = () => {
      if (lastCloseTimerStartTimeRef2.value < closeTimerStartTimeRef2.value) {
        const elapsedTime = (/* @__PURE__ */ new Date()).getTime() - closeTimerStartTimeRef2.value;
        remainingTime = remainingTime - elapsedTime;
      }
      lastCloseTimerStartTimeRef2.value = (/* @__PURE__ */ new Date()).getTime();
    };
    const startTimer = () => {
      if (remainingTime === Infinity)
        return;
      closeTimerStartTimeRef2.value = (/* @__PURE__ */ new Date()).getTime();
      timeoutId = setTimeout(() => {
        props2.toast.onAutoClose$?.(props2.toast);
        deleteToast2();
      }, remainingTime);
    };
    if (props2.expanded.value || props2.interacting || props2.pauseWhenPageIsHidden && isDocumentHidden2.value)
      pauseTimer();
    else
      startTimer();
    cleanup(() => clearTimeout(timeoutId));
  }, "Toast_component_useTask_1_g9KAbSZKhGM", [
    closeTimerStartTimeRef,
    deleteToast,
    duration,
    isDocumentHidden,
    lastCloseTimerStartTimeRef,
    props
  ]));
  useTaskQrl(/* @__PURE__ */ inlinedQrl(({ track, cleanup }) => {
    const [initialHeight2, props2, toastRef2] = useLexicalScope();
    track(() => toastRef2.value);
    track(() => props2.toast.id);
    if (!toastRef2.value)
      return;
    const height = toastRef2.value.getBoundingClientRect().height;
    initialHeight2.value = height;
    props2.heights.value = [
      {
        toastId: props2.toast.id,
        height,
        position: props2.toast.position ?? "bottom-right"
      },
      ...props2.heights.value
    ];
    cleanup(() => props2.heights.value = props2.heights.value.filter((h) => h.toastId !== props2.toast.id));
  }, "Toast_component_useTask_2_gi28gSpEk3Y", [
    initialHeight,
    props,
    toastRef
  ]));
  useTaskQrl(/* @__PURE__ */ inlinedQrl(({ track }) => {
    const [deleteToast2, props2] = useLexicalScope();
    track(() => props2.toast.delete);
    track(() => deleteToast2);
    if (props2.toast.delete)
      deleteToast2();
  }, "Toast_component_useTask_3_uR5X0KRPC5A", [
    deleteToast,
    props
  ]));
  useOnDocument("visibilitychange", /* @__PURE__ */ inlinedQrl(() => {
    const [isDocumentHidden2] = useLexicalScope();
    isDocumentHidden2.value = document.hidden;
  }, "Toast_component_useOnDocument_iXGbNUleynA", [
    isDocumentHidden
  ]));
  useVisibleTaskQrl(/* @__PURE__ */ inlinedQrl(({ track }) => {
    const [mounted2] = useLexicalScope();
    const localMount = track(() => mounted2.value);
    if (!localMount)
      mounted2.value = true;
  }, "Toast_component_useVisibleTask_bPVdCU20Qes", [
    mounted
  ]));
  return /* @__PURE__ */ _jsxQ("li", {
    ref: toastRef,
    class: [
      props.class ?? "",
      toastClass,
      props.classes?.toast,
      props.toast?.classes?.toast,
      props.classes?.default,
      props.classes?.[props.toast.type],
      props.toast?.classes?.[props.toast.type]
    ],
    "data-visible": String(isVisible),
    "data-y-position": y,
    "data-x-position": x,
    "data-front": String(isFront),
    "data-dismissible": String(dismissible),
    style: {
      "--index": props.index,
      "--toasts-before": props.index,
      "--z-index": props.toasts.length - props.index,
      "--offset": `${removed.value ? offsetBeforeRemove.value : offset.value}px`,
      "--initial-height": props.expandByDefault ? "auto" : `${initialHeight.value}px`,
      ...props.style,
      ...props.toast.style
    },
    onPointerDown$: /* @__PURE__ */ inlinedQrl((event, target) => {
      const [disabled2, dismissible2, dragStartTime2, offset2, offsetBeforeRemove2, pointerStartRef2, swiping2] = useLexicalScope();
      if (disabled2 || !dismissible2)
        return;
      dragStartTime2.value = /* @__PURE__ */ new Date();
      offsetBeforeRemove2.value = offset2.value;
      event.target.setPointerCapture(event.pointerId);
      if (target.tagName === "BUTTON")
        return;
      swiping2.value = true;
      pointerStartRef2.value = {
        x: event.clientX,
        y: event.clientY
      };
    }, "Toast_component_li_onPointerDown_0Qp5b6nbTLU", [
      disabled,
      dismissible,
      dragStartTime,
      offset,
      offsetBeforeRemove,
      pointerStartRef,
      swiping
    ]),
    onPointerUp$: /* @__PURE__ */ inlinedQrl(() => {
      const [deleteToast2, dismissible2, dragStartTime2, offset2, offsetBeforeRemove2, pointerStartRef2, props2, swipeOut2, swiping2, toastRef2] = useLexicalScope();
      if (swipeOut2.value || !dismissible2)
        return;
      pointerStartRef2.value = null;
      const swipeAmount = Number(toastRef2.value?.style.getPropertyValue("--swipe-amount").replace("px", "") ?? 0);
      const timeTaken = (/* @__PURE__ */ new Date()).getTime() - dragStartTime2.value?.getTime();
      const velocity = Math.abs(swipeAmount) / timeTaken;
      if (Math.abs(swipeAmount) >= SWIPE_THRESHOLD || velocity > 0.11) {
        offsetBeforeRemove2.value = offset2.value;
        props2.toast.onDismiss$?.(props2.toast);
        deleteToast2();
        swipeOut2.value = true;
        return;
      }
      toastRef2.value?.style.setProperty("--swipe-amount", "0px");
      swiping2.value = false;
    }, "Toast_component_li_onPointerUp_XKo0unq9QyI", [
      deleteToast,
      dismissible,
      dragStartTime,
      offset,
      offsetBeforeRemove,
      pointerStartRef,
      props,
      swipeOut,
      swiping,
      toastRef
    ]),
    onPointerMove$: /* @__PURE__ */ inlinedQrl((event) => {
      const [dismissible2, pointerStartRef2, toastRef2, y2] = useLexicalScope();
      if (!pointerStartRef2.value || !dismissible2)
        return;
      const yPosition = event.clientY - pointerStartRef2.value.y;
      const xPosition = event.clientX - pointerStartRef2.value.x;
      const clamp = y2 === "top" ? Math.min : Math.max;
      const clampedY = clamp(0, yPosition);
      const swipeStartThreshold = event.pointerType === "touch" ? 10 : 2;
      const isAllowedToSwipe = Math.abs(clampedY) > swipeStartThreshold;
      if (isAllowedToSwipe)
        toastRef2.value?.style.setProperty("--swipe-amount", `${yPosition}px`);
      else if (Math.abs(xPosition) > swipeStartThreshold)
        pointerStartRef2.value = null;
    }, "Toast_component_li_onPointerMove_k6kpAK0WJmM", [
      dismissible,
      pointerStartRef,
      toastRef,
      y
    ])
  }, {
    "aria-live": _fnSignal((p0) => p0.toast.important ? "assertive" : "polite", [
      props
    ], 'p0.toast.important?"assertive":"polite"'),
    "aria-atomic": "true",
    role: "status",
    tabIndex: 0,
    "data-qwik-toast": "",
    "data-styled": _fnSignal((p0) => String(!Boolean(p0.toast.jsx || p0.toast.unstyled || p0.unstyled)), [
      props
    ], "String(!Boolean(p0.toast.jsx||p0.toast.unstyled||p0.unstyled))"),
    "data-mounted": _fnSignal((p0) => String(p0.value), [
      mounted
    ], "String(p0.value)"),
    "data-promise": _fnSignal((p0) => String(Boolean(p0.toast.promise)), [
      props
    ], "String(Boolean(p0.toast.promise))"),
    "data-removed": _fnSignal((p0) => String(p0.value), [
      removed
    ], "String(p0.value)"),
    "data-index": _fnSignal((p0) => p0.index, [
      props
    ], "p0.index"),
    "data-swiping": _fnSignal((p0) => String(p0.value), [
      swiping
    ], "String(p0.value)"),
    "data-type": _fnSignal((p0) => p0.toast.type, [
      props
    ], "p0.toast.type"),
    "data-invert": _fnSignal((p0) => String(p0.value), [
      invert
    ], "String(p0.value)"),
    "data-swipe-out": _fnSignal((p0) => String(p0.value), [
      swipeOut
    ], "String(p0.value)"),
    "data-expanded": _fnSignal((p0, p1) => String(Boolean(p1.expanded.value || p1.expandByDefault && p0.value)), [
      mounted,
      props
    ], "String(Boolean(p1.expanded.value||p1.expandByDefault&&p0.value))")
  }, [
    closeButton.value && !props.toast.jsx ? /* @__PURE__ */ _jsxQ("button", {
      "data-disabled": String(disabled),
      onClick$: disabled || !dismissible ? () => {
      } : [
        deleteToast,
        /* @__PURE__ */ inlinedQrl(() => {
          const [props2] = useLexicalScope();
          return props2.toast.onDismiss$?.(props2.toast);
        }, "Toast_component_li_button_onClick_t8NnV05Urd8", [
          props
        ])
      ]
    }, {
      "aria-label": _fnSignal((p0) => p0.closeButtonAriaLabel ?? "Close toast", [
        props
      ], 'p0.closeButtonAriaLabel??"Close toast"'),
      "data-close-button": true,
      class: _fnSignal((p0) => [
        p0.classes?.closeButton,
        p0.toast?.classes?.closeButton
      ], [
        props
      ], "[p0.classes?.closeButton,p0.toast?.classes?.closeButton]")
    }, /* @__PURE__ */ _jsxQ("svg", null, {
      xmlns: "http://www.w3.org/2000/svg",
      width: "12",
      height: "12",
      viewBox: "0 0 24 24",
      fill: "none",
      stroke: "currentColor",
      "stroke-width": "1.5",
      "stroke-linecap": "round",
      "stroke-linejoin": "round"
    }, [
      /* @__PURE__ */ _jsxQ("line", null, {
        x1: "18",
        y1: "6",
        x2: "6",
        y2: "18"
      }, null, 3, null),
      /* @__PURE__ */ _jsxQ("line", null, {
        x1: "6",
        y1: "6",
        x2: "18",
        y2: "18"
      }, null, 3, null)
    ], 3, null), 2, "U0_3") : null,
    props.toast.jsx ?? typeof props.toast.title === "object" ? props.toast.jsx ?? props.toast.title : /* @__PURE__ */ _jsxC(Fragment, {
      children: [
        props.toast.type || props.toast.icon || props.toast.promise ? /* @__PURE__ */ _jsxQ("div", null, {
          "data-icon": "",
          class: _fnSignal((p0) => [
            p0.classes?.icon,
            p0.toast?.classes?.icon
          ], [
            props
          ], "[p0.classes?.icon,p0.toast?.classes?.icon]")
        }, [
          props.toast.promise || props.toast.type === "loading" && !props.toast.icon ? props.toast.icon ?? getLoadingIcon() : null,
          props.toast.type !== "loading" ? props.toast.icon ?? props.icons?.[props.toast.type] ?? getAsset(props.toast.type) : null
        ], 1, "U0_4") : null,
        /* @__PURE__ */ _jsxQ("div", null, {
          "data-content": "",
          class: _fnSignal((p0) => [
            p0.classes?.content,
            p0.toast?.classes?.content
          ], [
            props
          ], "[p0.classes?.content,p0.toast?.classes?.content]")
        }, [
          /* @__PURE__ */ _jsxQ("div", {
            dangerouslySetInnerHTML: sanitizeHTML(props.toast.title)
          }, {
            "data-title": "",
            class: _fnSignal((p0) => [
              p0.classes?.title,
              p0.toast?.classes?.title
            ], [
              props
            ], "[p0.classes?.title,p0.toast?.classes?.title]")
          }, null, 3, null),
          props.toast.description ? typeof props.toast.description === "string" ? /* @__PURE__ */ _jsxQ("div", {
            class: [
              props.descriptionClass ?? "",
              toastDescriptionClass,
              props.classes?.description,
              props.toast?.classes?.description
            ],
            dangerouslySetInnerHTML: sanitizeHTML(props.toast.description)
          }, {
            "data-description": ""
          }, null, 3, "U0_5") : /* @__PURE__ */ _jsxQ("div", {
            class: [
              props.descriptionClass ?? "",
              toastDescriptionClass,
              props.classes?.description,
              props.toast?.classes?.description
            ]
          }, {
            "data-description": ""
          }, _fnSignal((p0) => p0.toast.description, [
            props
          ], "p0.toast.description"), 3, null) : null
        ], 1, null),
        typeof props.toast.cancel === "function" ? props.toast.cancel : props.toast.cancel && isAction(props.toast.cancel) ? /* @__PURE__ */ _jsxQ("button", {
          onClick$: /* @__PURE__ */ inlinedQrl((event, target) => {
            const [deleteToast2, dismissible2, props2] = useLexicalScope();
            if (!isAction(props2.toast.cancel))
              return;
            if (!dismissible2)
              return;
            deleteToast2();
            if (props2.toast.cancel.onClick$)
              props2.toast.cancel.onClick$(event, target);
          }, "Toast_component_li__Fragment_button_onClick_N8XT5kw5nFI", [
            deleteToast,
            dismissible,
            props
          ])
        }, {
          "data-button": true,
          "data-cancel": true,
          style: _fnSignal((p0) => p0.toast.cancelButtonStyle ?? p0.cancelButtonStyle, [
            props
          ], "p0.toast.cancelButtonStyle??p0.cancelButtonStyle"),
          class: _fnSignal((p0) => [
            p0.classes?.cancelButton,
            p0.toast?.classes?.cancelButton
          ], [
            props
          ], "[p0.classes?.cancelButton,p0.toast?.classes?.cancelButton]")
        }, _fnSignal((p0) => p0.toast.cancel.label, [
          props
        ], "p0.toast.cancel.label"), 2, "U0_6") : null,
        typeof props.toast.action === "function" ? props.toast.action : props.toast.action && isAction(props.toast.action) ? /* @__PURE__ */ _jsxQ("button", null, {
          "data-button": "",
          style: _fnSignal((p0) => p0.toast.actionButtonStyle || p0.actionButtonStyle, [
            props
          ], "p0.toast.actionButtonStyle||p0.actionButtonStyle"),
          "preventdefault:click": _fnSignal((p0) => p0.toast.action.preventDefault, [
            props
          ], "p0.toast.action.preventDefault"),
          class: _fnSignal((p0) => [
            p0.classes?.actionButton,
            p0.toast?.classes?.actionButton
          ], [
            props
          ], "[p0.classes?.actionButton,p0.toast?.classes?.actionButton]"),
          onClick$: /* @__PURE__ */ inlinedQrl((event, target) => {
            const [deleteToast2, props2] = useLexicalScope();
            if (!isAction(props2.toast.action))
              return;
            props2.toast.action.onClick$(event, target);
            if (props2.toast.action.preventDefault)
              return;
            deleteToast2();
          }, "Toast_component_li__Fragment_button_onClick_1_rRcDtxY6IHc", [
            deleteToast,
            props
          ])
        }, _fnSignal((p0) => p0.toast.action.label, [
          props
        ], "p0.toast.action.label"), 3, "U0_7") : null
      ]
    }, 1, "U0_8")
  ], 0, "U0_9");
}, "Toast_component_0aOiYLLcTuA"));
const Toaster$1 = /* @__PURE__ */ componentQrl(/* @__PURE__ */ inlinedQrl((props) => {
  const toasts2 = useSignal([]);
  const heights = useSignal([]);
  const expanded = useSignal(false);
  const interacting = useSignal(false);
  const listRef = useSignal();
  const lastFocusedElementRef = useSignal(null);
  const isFocusWithinRef = useSignal(false);
  const possiblePositions = useComputedQrl(/* @__PURE__ */ inlinedQrl(() => {
    const [props2, toasts22] = useLexicalScope();
    return Array.from(new Set([
      props2.position ?? "bottom-right"
    ].concat(toasts22.value.filter((toast2) => toast2.position).map((toast2) => toast2.position))));
  }, "Toaster_component_possiblePositions_useComputed_aRGEq0pPEGI", [
    props,
    toasts2
  ]));
  const hotkeyLabel = (props.hotkey ?? [
    "altKey",
    "KeyT"
  ]).join("+").replace(/Key/g, "").replace(/Digit/g, "");
  const removeToast = /* @__PURE__ */ inlinedQrl((toast2) => {
    const [toasts22] = useLexicalScope();
    return toasts22.value = toasts22.value.filter(({ id }) => id !== toast2.id);
  }, "Toaster_component_removeToast_ZCIhQLvlQ0k", [
    toasts2
  ]);
  const onMountHandler = /* @__PURE__ */ inlinedQrl((_, _1) => {
    const [toasts22] = useLexicalScope();
    return ToastState.subscribe((toast2) => {
      if (toast2.dismiss) {
        toasts22.value = toasts22.value.map((t) => t.id === toast2.id ? {
          ...t,
          delete: true
        } : t);
        return;
      }
      const indexOfExistingToast = toasts22.value.findIndex((t) => t.id === toast2.id);
      if (indexOfExistingToast !== -1)
        return toasts22.value = [
          ...toasts22.value.slice(0, indexOfExistingToast),
          {
            ...toasts22.value[indexOfExistingToast],
            ...toast2
          },
          ...toasts22.value.slice(indexOfExistingToast + 1)
        ];
      return toasts22.value = [
        toast2,
        ...toasts22.value
      ];
    });
  }, "Toaster_component_onMountHandler_b0RHwa83Q6I", [
    toasts2
  ]);
  const onMountThemeHandler = /* @__PURE__ */ inlinedQrl((_, _1) => {
    const [listRef2] = useLexicalScope();
    const selectedTheme = listRef2.value?.getAttribute("data-theme");
    if (selectedTheme !== "system")
      return;
    const themeFromLocalStorage = localStorage.getItem("theme");
    if (themeFromLocalStorage)
      return listRef2.value?.setAttribute("data-theme", themeFromLocalStorage);
    const themeFromMediaQuery = window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
    listRef2.value?.setAttribute("data-theme", themeFromMediaQuery);
    localStorage.setItem("theme", themeFromMediaQuery);
  }, "Toaster_component_onMountThemeHandler_DSzPrlQthIQ", [
    listRef
  ]);
  const onMountDirHandler = /* @__PURE__ */ inlinedQrl((_, _1) => {
    const [listRef2, props2] = useLexicalScope();
    if (props2.dir && props2.dir !== "auto")
      return;
    const newDir = window.getComputedStyle(document.documentElement).direction;
    listRef2.value?.setAttribute("dir", newDir);
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === "attributes" && mutation.attributeName === "dir") {
          const value = mutation.target.getAttribute("dir");
          if (value === "auto" || !value)
            return listRef2.value.setAttribute("dir", newDir);
          listRef2.value.setAttribute("dir", value);
        }
      });
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: [
        "dir"
      ]
    });
  }, "Toaster_component_onMountDirHandler_z60vAP09fiw", [
    listRef,
    props
  ]);
  useTaskQrl(/* @__PURE__ */ inlinedQrl(({ track }) => {
    const [expanded2, toasts22] = useLexicalScope();
    track(() => toasts22.value);
    if (toasts22.value.length <= 1)
      expanded2.value = false;
  }, "Toaster_component_useTask_eJD7O1W6Dks", [
    expanded,
    toasts2
  ]));
  useOnDocument("keydown", /* @__PURE__ */ inlinedQrl((ev) => {
    const [expanded2, listRef2, props2] = useLexicalScope();
    const isHotkeyPressed = (props2.hotkey ?? [
      "altKey",
      "KeyT"
    ]).every((key) => ev[key] || ev.code === key);
    if (isHotkeyPressed) {
      expanded2.value = true;
      listRef2.value?.focus();
    }
    if (ev.code === "Escape" && (document.activeElement === listRef2.value || listRef2.value?.contains(document.activeElement)))
      expanded2.value = false;
  }, "Toaster_component_useOnDocument_hiFJasaL4yY", [
    expanded,
    listRef,
    props
  ]));
  useTaskQrl(/* @__PURE__ */ inlinedQrl(({ track, cleanup }) => {
    const [isFocusWithinRef2, lastFocusedElementRef2, listRef2] = useLexicalScope();
    track(() => listRef2.value);
    if (!listRef2.value)
      return;
    cleanup(() => {
      if (lastFocusedElementRef2.value) {
        lastFocusedElementRef2.value.focus({
          preventScroll: true
        });
        lastFocusedElementRef2.value = null;
        isFocusWithinRef2.value = false;
      }
    });
  }, "Toaster_component_useTask_1_aHq5phOi7kM", [
    isFocusWithinRef,
    lastFocusedElementRef,
    listRef
  ]));
  return (
    // Remove item from normal navigation flow, only available via hotkey
    /* @__PURE__ */ _jsxQ("section", {
      "aria-label": `${props.containerAriaLabel ?? "Notifications"} ${hotkeyLabel}`
    }, {
      tabIndex: -1,
      onQVisible$: [
        onMountHandler,
        onMountThemeHandler,
        onMountDirHandler
      ]
    }, possiblePositions.value.map((position, index) => {
      const [y, x] = position.split("-");
      return /* @__PURE__ */ _jsxQ("ol", {
        ref: listRef,
        "data-y-position": y,
        "data-x-position": x,
        style: {
          "--front-toast-height": `${heights.value[0]?.height ?? 0}px`,
          "--offset": typeof props.offset === "number" ? `${props.offset}px` : props.offset ?? VIEWPORT_OFFSET,
          "--width": `${TOAST_WIDTH}px`,
          "--gap": `${props.gap ?? GAP}px`,
          ...props.style
        }
      }, {
        dir: _fnSignal((p0) => p0.dir, [
          props
        ], "p0.dir"),
        tabIndex: -1,
        class: _fnSignal((p0) => p0.class, [
          props
        ], "p0.class"),
        "data-qwik-toaster": true,
        "data-theme": _fnSignal((p0) => p0.theme ?? "light", [
          props
        ], 'p0.theme??"light"'),
        "data-rich-colors": _fnSignal((p0) => String(p0.richColors), [
          props
        ], "String(p0.richColors)"),
        onBlur$: /* @__PURE__ */ inlinedQrl((event, target) => {
          const [isFocusWithinRef2, lastFocusedElementRef2] = useLexicalScope();
          if (isFocusWithinRef2.value && !target.contains(event.relatedTarget)) {
            isFocusWithinRef2.value = false;
            if (lastFocusedElementRef2.value) {
              lastFocusedElementRef2.value.focus({
                preventScroll: true
              });
              lastFocusedElementRef2.value = null;
            }
          }
        }, "Toaster_component_section_ol_onBlur_oPDUtqqA9NM", [
          isFocusWithinRef,
          lastFocusedElementRef
        ]),
        onFocusIn$: /* @__PURE__ */ inlinedQrl((event, target) => {
          const [isFocusWithinRef2, lastFocusedElementRef2] = useLexicalScope();
          const isNotDismissible = target instanceof HTMLElement && target.dataset.dismissible === "false";
          if (isNotDismissible)
            return;
          if (!isFocusWithinRef2.value) {
            isFocusWithinRef2.value = true;
            lastFocusedElementRef2.value = event.relatedTarget;
          }
        }, "Toaster_component_section_ol_onFocusIn_I6YONFTIFiM", [
          isFocusWithinRef,
          lastFocusedElementRef
        ]),
        onMouseEnter$: /* @__PURE__ */ inlinedQrl(() => {
          const [expanded2] = useLexicalScope();
          return expanded2.value = true;
        }, "Toaster_component_section_ol_onMouseEnter_RRMobuad0OM", [
          expanded
        ]),
        onMouseMove$: /* @__PURE__ */ inlinedQrl(() => {
          const [expanded2] = useLexicalScope();
          return expanded2.value = true;
        }, "Toaster_component_section_ol_onMouseMove_dxQo6jvuA6w", [
          expanded
        ]),
        onMouseLeave$: /* @__PURE__ */ inlinedQrl(() => {
          const [expanded2, interacting2] = useLexicalScope();
          if (!interacting2.value)
            expanded2.value = false;
        }, "Toaster_component_section_ol_onMouseLeave_4tsR9rwTp24", [
          expanded,
          interacting
        ]),
        onPointerDown$: /* @__PURE__ */ inlinedQrl((_, target) => {
          const [interacting2] = useLexicalScope();
          const isNotDismissible = target instanceof HTMLElement && target.dataset.dismissible === "false";
          if (isNotDismissible)
            return;
          interacting2.value = true;
        }, "Toaster_component_section_ol_onPointerDown_Ze98mGqnscA", [
          interacting
        ]),
        onPointerUp$: /* @__PURE__ */ inlinedQrl(() => {
          const [interacting2] = useLexicalScope();
          return interacting2.value = false;
        }, "Toaster_component_section_ol_onPointerUp_kKUsA2BuTZ4", [
          interacting
        ])
      }, toasts2.value.filter((toast2) => !toast2.position && index === 0 || toast2.position === position).map((toast2, index2) => /* @__PURE__ */ _jsxC(Toast, {
        get icons() {
          return props.icons;
        },
        index: index2,
        toast: toast2,
        get duration() {
          return props.toastOptions?.duration ?? props.duration;
        },
        get class() {
          return props.toastOptions?.class;
        },
        get descriptionClass() {
          return props.toastOptions?.descriptionClass;
        },
        get invert() {
          return props.invert ?? false;
        },
        visibleToasts: props.visibleToasts ?? VISIBLE_TOASTS_AMOUNT,
        get closeButton() {
          return props.toastOptions?.closeButton ?? props.closeButton ?? false;
        },
        get interacting() {
          return interacting.value;
        },
        position,
        get style() {
          return props.toastOptions?.style;
        },
        get unstyled() {
          return props.toastOptions?.unstyled;
        },
        get classes() {
          return props.toastOptions?.classes;
        },
        get cancelButtonStyle() {
          return props.toastOptions?.cancelButtonStyle;
        },
        get actionButtonStyle() {
          return props.toastOptions?.actionButtonStyle;
        },
        removeToast,
        toasts: toasts2.value.filter((t) => t.position === toast2.position),
        heights,
        get expandByDefault() {
          return props.expand ?? false;
        },
        gap: props.gap ?? GAP,
        get loadingIcon() {
          return props.loadingIcon;
        },
        expanded,
        get pauseWhenPageIsHidden() {
          return props.pauseWhenPageIsHidden ?? false;
        },
        [_IMMUTABLE]: {
          icons: _fnSignal((p0) => p0.icons, [
            props
          ], "p0.icons"),
          duration: _fnSignal((p0) => p0.toastOptions?.duration ?? p0.duration, [
            props
          ], "p0.toastOptions?.duration??p0.duration"),
          class: _fnSignal((p0) => p0.toastOptions?.class, [
            props
          ], "p0.toastOptions?.class"),
          descriptionClass: _fnSignal((p0) => p0.toastOptions?.descriptionClass, [
            props
          ], "p0.toastOptions?.descriptionClass"),
          invert: _fnSignal((p0) => p0.invert ?? false, [
            props
          ], "p0.invert??false"),
          closeButton: _fnSignal((p0) => p0.toastOptions?.closeButton ?? p0.closeButton ?? false, [
            props
          ], "p0.toastOptions?.closeButton??p0.closeButton??false"),
          interacting: _fnSignal((p0) => p0.value, [
            interacting
          ], "p0.value"),
          style: _fnSignal((p0) => p0.toastOptions?.style, [
            props
          ], "p0.toastOptions?.style"),
          unstyled: _fnSignal((p0) => p0.toastOptions?.unstyled, [
            props
          ], "p0.toastOptions?.unstyled"),
          classes: _fnSignal((p0) => p0.toastOptions?.classes, [
            props
          ], "p0.toastOptions?.classes"),
          cancelButtonStyle: _fnSignal((p0) => p0.toastOptions?.cancelButtonStyle, [
            props
          ], "p0.toastOptions?.cancelButtonStyle"),
          actionButtonStyle: _fnSignal((p0) => p0.toastOptions?.actionButtonStyle, [
            props
          ], "p0.toastOptions?.actionButtonStyle"),
          removeToast: _IMMUTABLE,
          heights: _IMMUTABLE,
          expandByDefault: _fnSignal((p0) => p0.expand ?? false, [
            props
          ], "p0.expand??false"),
          loadingIcon: _fnSignal((p0) => p0.loadingIcon, [
            props
          ], "p0.loadingIcon"),
          expanded: _IMMUTABLE,
          pauseWhenPageIsHidden: _fnSignal((p0) => p0.pauseWhenPageIsHidden ?? false, [
            props
          ], "p0.pauseWhenPageIsHidden??false")
        }
      }, 3, toast2.id)), 1, position);
    }), 1, "L9_0")
  );
}, "Toaster_component_IPk10GunI7g"));
const Toaster = /* @__PURE__ */ componentQrl(/* @__PURE__ */ inlinedQrl((props) => {
  useStylesQrl(/* @__PURE__ */ inlinedQrl(styles, "Toaster_component_useStyles_skL3E556KHA"));
  return /* @__PURE__ */ _jsxC(Toaster$1, {
    ...props
  }, 0, "8G_0");
}, "Toaster_component_OAwbeyfgs0c"));
export {
  Toaster,
  toast
};
